var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import { isSameDay } from 'date-fns';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownSingleSelect } from '../lift-dropdown/dropdown-single-select';
import { cn } from '../lib/utils';
import { CalendarIcon } from 'lucide-react';
import SvgCalendar from '../ui/icons/Calendar';
import SvgInfoCircle from '../ui/icons/InfoCircle';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import 'react-calendar/dist/Calendar.css';
import "../../styles/react-calendar-tailwind.css";
import { addDays, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, subMonths, subQuarters, subYears, } from "date-fns";
import { Toastify } from '../ui/toast';
import moment from "moment";
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown } from '../ui/icons';
import { Switch } from '../ui/switch';
import { ChevronUp } from 'lucide-react';
// Utility helpers for date calculations
// Safely convert various inputs (Date, Firestore Timestamp, Dayjs, string, number) to a native Date instance
var toDate = function (value) {
    if (value instanceof Date)
        return value;
    // Handle objects that expose a toDate() converter (e.g. Firebase Timestamp or Day.js)
    if (value && typeof value.toDate === 'function') {
        var d = value.toDate();
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
    }
    // Fall-back for primitive representations
    var parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
};
var startOfDay = function (date) {
    var d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};
var addDays1 = function (date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};
var formatDisplayDate = function (input) {
    var date = toDate(input);
    return date
        ? date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        })
        : '';
};
// Get start of a week based on a given weekStart (0 = Sun .. 6 = Sat)
var startOfWeek1 = function (date, weekStart) {
    var day = date.getDay();
    var diff = (day - weekStart + 7) % 7;
    return startOfDay(addDays1(date, -diff));
};
// Generate CSS for reordering calendar days based on weekStartsOn
// Since react-calendar doesn't support arbitrary week starts, we use CSS Grid to place items
var getWeekReorderStyles = function (weekStartsOn, calendarId) {
    // iso8601 (Monday-start) positions: Mon=0, Tue=1, Wed=2, Thu=3, Fri=4, Sat=5, Sun=6
    // weekStartsOn uses: Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
    // Convert weekStartsOn to iso8601 position (shift amount)
    var shift = (weekStartsOn + 6) % 7;
    // Header reordering (flexbox with order works for single row)
    var headerOrders = [];
    for (var origPos = 0; origPos < 7; origPos++) {
        var newOrder = (origPos - shift + 7) % 7;
        headerOrders.push("\n      #".concat(calendarId, " .react-calendar__month-view__weekdays__weekday:nth-child(").concat(origPos + 1, ") {\n        order: ").concat(newOrder, " !important;\n        flex: 1 !important;\n        text-align: center !important;\n      }\n    "));
    }
    // Column classes for day tiles (which column based on day of week)
    var columnRules = [];
    for (var dayOfWeek = 0; dayOfWeek <= 6; dayOfWeek++) {
        // Calculate which column this day should be in
        var column = (dayOfWeek - weekStartsOn + 7) % 7 + 1; // +1 because grid columns are 1-indexed
        columnRules.push("\n      #".concat(calendarId, " .weekday-").concat(dayOfWeek, " {\n        grid-column: ").concat(column, " !important;\n      }\n    "));
    }
    // Row classes for day tiles (up to 6 rows max in a month)
    var rowRules = [];
    for (var row = 0; row <= 5; row++) {
        rowRules.push("\n      #".concat(calendarId, " .weekrow-").concat(row, " {\n        grid-row: ").concat(row + 1, " !important;\n      }\n    "));
    }
    return "\n    #".concat(calendarId, " .react-calendar__month-view__weekdays {\n      display: flex !important;\n      width: 100% !important;\n    }\n    #").concat(calendarId, " .react-calendar__month-view__days {\n      display: grid !important;\n      grid-template-columns: repeat(7, 1fr) !important;\n    }\n    #").concat(calendarId, " .react-calendar__month-view__days button {\n      justify-self: center !important;\n    }\n    ").concat(headerOrders.join(''), "\n    ").concat(columnRules.join(''), "\n    ").concat(rowRules.join(''), "\n  ");
};
// Calculate which row a date belongs to for the custom week start
var getWeekRow = function (date, weekStartsOn) {
    var year = date.getFullYear();
    var month = date.getMonth();
    var dayOfMonth = date.getDate();
    // Get the day of week of the 1st of the month (0=Sun, 1=Mon, ..., 6=Sat)
    var firstDayOfWeek = new Date(year, month, 1).getDay();
    // Calculate how many days of the first week are in the previous month
    var daysBefore1stInFirstWeek = (firstDayOfWeek - weekStartsOn + 7) % 7;
    // Calculate the row (0-indexed)
    return Math.floor((dayOfMonth + daysBefore1stInFirstWeek - 1) / 7);
};
// NEW: Function to evaluate date expressions
var evaluateDateExpression = function (expression, variables) {
    if (expression === "N/A")
        return null;
    try {
        // Handle arithmetic operations first (e.g., "today-1", "startOfWeekSunday+6")
        var arithmeticMatch = expression.match(/^([a-zA-Z]+)([+-])(\d+)$/);
        if (arithmeticMatch) {
            var varName = arithmeticMatch[1], operator = arithmeticMatch[2], days = arithmeticMatch[3];
            var baseDate = variables[varName];
            if (baseDate) {
                var daysNum = parseInt(days, 10);
                if (operator === '+') {
                    return addDays(baseDate, daysNum);
                }
                else {
                    return subDays(baseDate, daysNum);
                }
            }
        }
        // Handle direct variable references
        if (variables[expression]) {
            return variables[expression];
        }
        // Handle complex expressions by creating a context and evaluating
        var context = __assign(__assign({}, variables), { startOfWeek: startOfWeek, endOfWeek: endOfWeek, startOfMonth: startOfMonth, endOfMonth: endOfMonth, startOfQuarter: startOfQuarter, endOfQuarter: endOfQuarter, startOfYear: startOfYear, endOfYear: endOfYear, subMonths: subMonths, subQuarters: subQuarters, subYears: subYears, addDays: addDays, subDays: subDays, Date: Date });
        // Create a function that evaluates the expression in the given context
        var func = new (Function.bind.apply(Function, __spreadArray(__spreadArray([void 0], Object.keys(context), false), ["return ".concat(expression)], false)))();
        var result = func.apply(void 0, Object.values(context));
        return result instanceof Date ? result : null;
    }
    catch (error) {
        console.error('Error evaluating date expression:', expression, error);
        return null;
    }
};
// NEW: Function to create date variables from datePresetVar
var createDateVariables = function (datePresetVar) {
    var variables = {};
    var context = {
        startOfWeek: startOfWeek,
        endOfWeek: endOfWeek,
        startOfMonth: startOfMonth,
        endOfMonth: endOfMonth,
        startOfQuarter: startOfQuarter,
        endOfQuarter: endOfQuarter,
        startOfYear: startOfYear,
        endOfYear: endOfYear,
        subMonths: subMonths,
        subQuarters: subQuarters,
        subYears: subYears,
        addDays: addDays,
        subDays: subDays,
        Date: Date,
    };
    for (var _i = 0, _a = Object.entries(datePresetVar); _i < _a.length; _i++) {
        var _b = _a[_i], key = _b[0], expression = _b[1];
        try {
            var func = new (Function.bind.apply(Function, __spreadArray(__spreadArray([void 0], Object.keys(context), false), ["return ".concat(expression)], false)))();
            var result = func.apply(void 0, Object.values(context));
            if (result instanceof Date) {
                variables[key] = result;
            }
        }
        catch (error) {
            console.error('Error creating date variable:', key, expression, error);
        }
    }
    return variables;
};
function getDateInBothTimeZones(dateString) {
    // Parse the input date string (assumes YYYY-MM-DD format)
    var _a = dateString.split("-").map(Number), year = _a[0], month = _a[1], day = _a[2];
    var date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    return date;
}
function isSameDateInIndiaAndUSA(date) {
    // Get current date in India (IST)
    var indiaDate = date.toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
    });
    // Get current date in USA (EST)
    var usaDate = date.toLocaleString("en-US", {
        timeZone: "America/New_York",
    });
    // Extract only the date part (ignoring time)
    var indiaDateOnly = indiaDate.split(",")[0];
    var usaDateOnly = usaDate.split(",")[0];
    // Compare the dates
    console.log(indiaDateOnly, usaDateOnly, "usaDateOnly-indiaDateOnly");
}
// NEW: Function to generate quick ranges from datePresetInfo
function getPreviousQuarter(quarter) {
    switch (quarter) {
        case "Q1":
            return "Q4"; // Previous quarter of Q1 is Q4 of the previous year
        case "Q2":
            return "Q1"; // Previous quarter of Q2 is Q1 of the same year
        case "Q3":
            return "Q2"; // Previous quarter of Q3 is Q2 of the same year
        case "Q4":
            return "Q3"; // Previous quarter of Q4 is Q3 of the same year
        default:
            throw new Error("Invalid quarter number. Use Q1, Q2, Q3, or Q4.");
    }
}
var compareOptions = [
    { id: 'preceding', label: 'Preceding period' },
    { id: 'precedingMatchDay', label: 'Preceding period', subtext: 'match day of week' },
    { id: 'lastYearMatchDay', label: 'Same period last year', subtext: 'match day of week' },
    { id: 'lastYear', label: 'Same period last year' },
    { id: 'same', label: 'Custom period', subtext: 'select start' },
    { id: 'custom', label: 'Custom' },
];
function monthToIndex(monthName) {
    var months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    return months.indexOf(monthName);
}
// Ensure an incoming tuple is converted to a tuple of valid Date objects
var sanitizeRange = function (input) { return [toDate(input[0]) || new Date(), toDate(input[1]) || new Date()]; };
var DateRangePicker = function (_a) {
    var initialStartDate = _a.initialStartDate, initialEndDate = _a.initialEndDate, initialCompareStartDate = _a.initialCompareStartDate, initialCompareEndDate = _a.initialCompareEndDate, _b = _a.initialCompareMode, initialCompareMode = _b === void 0 ? 'preceding' : _b, onApply = _a.onApply, _c = _a.fiscal, fiscal = _c === void 0 ? false : _c, _d = _a.yearType, yearType = _d === void 0 ? 'calendar' : _d, onYearTypeChange = _a.onYearTypeChange, datePresetInfo = _a.datePresetInfo, datePresetVar = _a.datePresetVar, fiscalData = _a.fiscalData, brandId = _a.brandId, defaultDatePreset = _a.defaultDatePreset, initalLoad = _a.initalLoad, selectedDatePreset = _a.selectedDatePreset, _e = _a.showCompareToggle, showCompareToggle = _e === void 0 ? false : _e, _f = _a.forceCompareOn, forceCompareOn = _f === void 0 ? false : _f, _g = _a.disabledCompareModes, disabledCompareModes = _g === void 0 ? [] : _g, _h = _a.disabledCompareText, disabledCompareText = _h === void 0 ? "Coming Soon" : _h, _j = _a.presetOnly, presetOnly = _j === void 0 ? false : _j, _k = _a.showRelative, showRelative = _k === void 0 ? false : _k, relativeOptionsProp = _a.relativeOptions, onRelativeSelect = _a.onRelativeSelect, selectedRelativeOption = _a.selectedRelativeOption, _l = _a.weekStartsOn, weekStartsOn = _l === void 0 ? 1 : _l, maxDate = _a.maxDate;
    // Global selection state for multi-month selection
    var _m = useState({
        isSelecting: false,
        startDate: null,
        currentRange: null,
        editingRange: 'primary',
        hoverDate: null,
        previousRange: null,
        previousCompareRange: null
    }), selectionState = _m[0], setSelectionState = _m[1];
    // Helper function to reset selection state
    var resetSelectionState = function (editingRange) {
        if (editingRange === void 0) { editingRange = 'primary'; }
        return ({
            isSelecting: false,
            startDate: null,
            currentRange: null,
            editingRange: editingRange,
            hoverDate: null
        });
    };
    // Default relative date options matching backend ComparisionDate enum (query_executor.py)
    // - "This" periods: start of period → today (inclusive)
    // - "Last" periods: full previous period (Mon-Sun week)
    // - "Last N days": today-N → yesterday (today excluded)
    // - Week = Monday–Sunday
    var defaultRelativeOptions = React.useMemo(function () {
        var today = startOfDay(new Date());
        var yesterday = subDays(today, 1);
        return [
            // --- Single day ---
            {
                id: 'today',
                label: 'Today',
                getRange: function () { return [today, today]; },
            },
            {
                id: 'yesterday',
                label: 'Yesterday',
                getRange: function () { return [yesterday, yesterday]; },
            },
            // --- "This" period: start of period → today ---
            {
                id: 'this_week',
                label: 'This Week',
                getRange: function () { return [startOfWeek(today, { weekStartsOn: weekStartsOn }), today]; },
            },
            {
                id: 'this_month',
                label: 'This Month',
                getRange: function () { return [startOfMonth(today), today]; },
            },
            {
                id: 'this_quarter',
                label: 'This Quarter',
                getRange: function () { return [startOfQuarter(today), today]; },
            },
            {
                id: 'this_year',
                label: 'This Year',
                getRange: function () { return [startOfYear(today), today]; },
            },
            // --- "Last" full period: previous Mon→Sun / full prev month / etc. ---
            {
                id: 'last_week',
                label: 'Last Week',
                getRange: function () {
                    var thisWeekStart = startOfWeek(today, { weekStartsOn: weekStartsOn });
                    return [subDays(thisWeekStart, 7), subDays(thisWeekStart, 1)];
                },
            },
            {
                id: 'last_month',
                label: 'Last Month',
                getRange: function () {
                    var d = subMonths(today, 1);
                    return [startOfMonth(d), endOfMonth(d)];
                },
            },
            {
                id: 'last_quarter',
                label: 'Last Quarter',
                getRange: function () {
                    var prevQuarterStart = subMonths(startOfQuarter(today), 3);
                    return [prevQuarterStart, subDays(startOfQuarter(today), 1)];
                },
            },
            {
                id: 'last_year',
                label: 'Last Year',
                getRange: function () {
                    var d = subYears(today, 1);
                    return [startOfYear(d), endOfYear(d)];
                },
            },
            // --- Rolling "last N days": today-N → yesterday (today excluded) ---
            {
                id: 'last_2_days',
                label: 'Last 2 Days',
                getRange: function () { return [subDays(today, 2), yesterday]; },
            },
            {
                id: 'last_3_days',
                label: 'Last 3 Days',
                getRange: function () { return [subDays(today, 3), yesterday]; },
            },
            {
                id: 'last_4_days',
                label: 'Last 4 Days',
                getRange: function () { return [subDays(today, 4), yesterday]; },
            },
            {
                id: 'last_7_days',
                label: 'Last 7 Days',
                getRange: function () { return [subDays(today, 7), yesterday]; },
            },
            {
                id: 'last_9_days',
                label: 'Last 9 Days',
                getRange: function () { return [subDays(today, 9), yesterday]; },
            },
            {
                id: 'last_14_days',
                label: 'Last 14 Days',
                getRange: function () { return [subDays(today, 14), yesterday]; },
            },
            {
                id: 'last_28_days',
                label: 'Last 28 Days',
                getRange: function () { return [subDays(today, 28), yesterday]; },
            },
            {
                id: 'last_30_days',
                label: 'Last 30 Days',
                getRange: function () { return [subDays(today, 30), yesterday]; },
            },
            {
                id: 'last_60_days',
                label: 'Last 60 Days',
                getRange: function () { return [subDays(today, 60), yesterday]; },
            },
            {
                id: 'last_90_days',
                label: 'Last 90 Days',
                getRange: function () { return [subDays(today, 90), yesterday]; },
            },
            {
                id: 'last_180_days',
                label: 'Last 180 Days',
                getRange: function () { return [subDays(today, 180), yesterday]; },
            },
            {
                id: 'last_365_days',
                label: 'Last 365 Days',
                getRange: function () { return [subDays(today, 365), yesterday]; },
            },
            {
                id: 'last_730_days',
                label: 'Last 730 Days',
                getRange: function () { return [subDays(today, 730), yesterday]; },
            },
            // --- Other ---
            {
                id: 'latest_4_quarters',
                label: 'Latest 4 Quarters',
                getRange: function () { return [subDays(today, 365), today]; },
            },
        ];
    }, []);
    var relativeOptions = relativeOptionsProp || defaultRelativeOptions;
    // State for selected relative option
    var _o = useState(selectedRelativeOption || ''), internalRelativeOption = _o[0], setInternalRelativeOption = _o[1];
    // Sync internal state with controlled prop
    useEffect(function () {
        if (selectedRelativeOption !== undefined) {
            setInternalRelativeOption(selectedRelativeOption);
        }
    }, [selectedRelativeOption]);
    // Handle relative option selection
    var handleRelativeSelect = function (optionId) {
        setInternalRelativeOption(optionId);
        var option = relativeOptions.find(function (o) { return o.id === optionId; });
        if (option) {
            var _a = option.getRange(), start = _a[0], end = _a[1];
            setRange([start, end]);
            setActiveQuick(option.label);
            if (onRelativeSelect) {
                onRelativeSelect(optionId, option.label, start, end);
            }
            if (onApply) {
                onApply(start, end, option.label, undefined, undefined, undefined);
            }
        }
    };
    var generateQuickRanges = function (datePresetInfo, datePresetVar, yearType) {
        var variables = createDateVariables(datePresetVar);
        var ranges = [];
        var curDate = new Date();
        var curDayOfWeek = curDate.getDay();
        var offsetToPreviousMonday = curDayOfWeek === 0 ? -6 : 1 - curDayOfWeek;
        var offsetToPreviousSunday = -curDayOfWeek;
        var offsetToPreviousTuesday = curDayOfWeek === 2 ? 0 : curDayOfWeek === 0 ? -5 : 2 - curDayOfWeek;
        var offsetToPreviousWednesday = curDayOfWeek === 3 ? 0 : curDayOfWeek === 0 ? -6 : 3 - curDayOfWeek;
        var _loop_1 = function (preset) {
            // Skip Custom Range as it doesn't have a predefined range
            if (preset.value === 0)
                return "continue";
            if (yearType === 'fiscal' && preset.fiscalView && preset.fiscalView == "FALSE") {
                return "continue";
            }
            // if(yearType === 'calendar' && preset.fiscalView){
            //   continue;
            // }
            // Determine which dates to use based on yearType and fiscalEffect
            var shouldUseFiscalDates = yearType === 'fiscal' && (preset.fiscalEffect === "TRUE" || preset.fiscalEffect === true);
            var startExpr = shouldUseFiscalDates && preset.value !== 29 && preset.fiscalStartDate ? preset.fiscalStartDate : preset.startDate;
            var endExpr = shouldUseFiscalDates && preset.value !== 29 && preset.fiscalEndDate ? preset.fiscalEndDate : preset.endDate;
            var startDate_1 = evaluateDateExpression(startExpr, variables);
            var endDate_1 = evaluateDateExpression(endExpr, variables);
            if (shouldUseFiscalDates) {
                switch (preset.value) {
                    case 2:
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            if (quarterNumber) {
                                var startDate1 = getDateInBothTimeZones(quarterNumber.week_start_date.split("T")[0]);
                                var endDate1 = getDateInBothTimeZones(quarterNumber.week_end_date.split("T")[0]);
                                endDate1.setDate(endDate1.getDate());
                                startDate1.setDate(startDate1.getDate());
                                startDate_1 = startDate1;
                                endDate_1 = endDate1;
                                break;
                            }
                        }
                        break;
                    case 3:
                        console.log("This Month");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            console.log(quarterNumber, "quarterNumber");
                            if (quarterNumber) {
                                var startDate1 = getDateInBothTimeZones(quarterNumber.month_start_date.split("T")[0]);
                                var endDate1 = getDateInBothTimeZones(quarterNumber.month_end_date.split("T")[0]);
                                startDate_1 = startDate1;
                                endDate_1 = endDate1;
                                break;
                            }
                        }
                        break;
                    case 4:
                        console.log("This Quarter");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber_1 = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            console.log(quarterNumber_1, "quarterNumber");
                            if (quarterNumber_1) {
                                var quaterData = fiscalData.filter(function (item) {
                                    return item.quarter == quarterNumber_1.quarter &&
                                        item.year == quarterNumber_1.year;
                                });
                                console.log(quaterData, fiscalData, "quaterData");
                                if (quaterData && quaterData.length) {
                                    // let startDate = new Date(quaterData[0].month_start_date);
                                    var startDate1 = getDateInBothTimeZones(quaterData[0].month_start_date.split("T")[0]);
                                    var endDate1 = getDateInBothTimeZones(quaterData[quaterData.length - 1].month_end_date.split("T")[0]);
                                    startDate_1 = startDate1;
                                    endDate_1 = endDate1;
                                }
                                break;
                            }
                        }
                        break;
                    case 5:
                        console.log("This Year");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber_2 = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            console.log(curDate, quarterNumber_2, "quarterNumber");
                            if (quarterNumber_2) {
                                var quaterData = fiscalData.filter(function (item) { return item.year == quarterNumber_2.year; });
                                console.log(quaterData, fiscalData, "quaterData");
                                if (quaterData && quaterData.length) {
                                    // let startDate = new Date(quaterData[0].month_start_date);
                                    var startDate1 = getDateInBothTimeZones(quaterData[0].month_start_date.split("T")[0]);
                                    // let endDate = new Date(
                                    //   quaterData[quaterData.length - 1].month_end_date
                                    // );
                                    var endDate1 = getDateInBothTimeZones(quaterData[quaterData.length - 1].month_end_date.split("T")[0]);
                                    startDate_1 = startDate1;
                                    endDate_1 = endDate1;
                                }
                                break;
                            }
                        }
                        break;
                    case 6:
                        console.log("Last Week");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            curDate.setDate(new Date().getDate() - 7);
                            var quarterNumber = fiscalData.find(function (item) {
                                return moment(curDate).isBetween(moment(item.week_start_date).startOf("day"), moment(item.week_end_date).endOf("day"), undefined, "[]");
                            });
                            if (quarterNumber) {
                                // let startDate = new Date(quarterNumber.week_start_date);
                                var startDate1 = getDateInBothTimeZones(quarterNumber.week_start_date.split("T")[0]);
                                // let endDate = new Date(quarterNumber.week_end_date);
                                var endDate1 = getDateInBothTimeZones(quarterNumber.week_end_date.split("T")[0]);
                                endDate1.setDate(endDate1.getDate());
                                startDate1.setDate(startDate1.getDate());
                                startDate_1 = startDate1;
                                endDate_1 = endDate1;
                            }
                        }
                        break;
                    case 7:
                        console.log("Last Month");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber_3 = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            console.log(quarterNumber_3, "quarterNumber");
                            if (quarterNumber_3) {
                                var weekMonth = monthToIndex(quarterNumber_3.month);
                                // Calculate the previous month (0-11, where 0 is January)
                                var previousMonth = weekMonth === 0 ? 11 : weekMonth - 1;
                                // Find the corresponding month name
                                var monthNames = [
                                    "January",
                                    "February",
                                    "March",
                                    "April",
                                    "May",
                                    "June",
                                    "July",
                                    "August",
                                    "September",
                                    "October",
                                    "November",
                                    "December",
                                ];
                                var previousMonthName_1 = monthNames[previousMonth];
                                // Find the row in fiscalData for the previous month
                                var previousMonthData = fiscalData.find(function (item) {
                                    return item.month == previousMonthName_1 &&
                                        item.year == quarterNumber_3.year;
                                });
                                if (previousMonthData) {
                                    var startDate1 = getDateInBothTimeZones(previousMonthData.month_start_date.split("T")[0]);
                                    var endDate1 = getDateInBothTimeZones(previousMonthData.month_end_date.split("T")[0]);
                                    console.log(startDate_1, "startDate");
                                    isSameDateInIndiaAndUSA(startDate1);
                                    startDate_1 = startDate1;
                                    endDate_1 = endDate1;
                                }
                                break;
                            }
                        }
                        break;
                    case 8:
                        console.log("Last quarter");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            console.log(quarterNumber, "quarterNumber");
                            if (quarterNumber) {
                                var previousQuater_1 = getPreviousQuarter(quarterNumber.quarter);
                                var quaterYear_1 = quarterNumber.quarter === "Q1"
                                    ? quarterNumber.year - 1
                                    : quarterNumber.year;
                                var quaterData = fiscalData.filter(function (item) {
                                    return item.quarter == previousQuater_1 && item.year == quaterYear_1;
                                });
                                console.log(quaterData, fiscalData, "quaterData");
                                if (quaterData && quaterData.length) {
                                    // let startDate = new Date(quaterData[0].month_start_date);
                                    var startDate1 = getDateInBothTimeZones(quaterData[0].month_start_date.split("T")[0]);
                                    // let endDate = new Date(
                                    //   quaterData[quaterData.length - 1].month_end_date
                                    // );
                                    var endDate1 = getDateInBothTimeZones(quaterData[quaterData.length - 1].month_end_date.split("T")[0]);
                                    startDate_1 = startDate1;
                                    endDate_1 = endDate1;
                                }
                                break;
                            }
                        }
                        break;
                    case 9:
                        console.log("Last Year");
                        if (yearType === 'fiscal' && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) > 0) {
                            var quarterNumber_4 = fiscalData.find(function (item) {
                                var weekStartDate = new Date(item.week_start_date);
                                var weekEndDate = new Date(item.week_end_date);
                                return curDate >= weekStartDate && curDate <= weekEndDate;
                            });
                            console.log(quarterNumber_4, "quarterNumber");
                            if (quarterNumber_4) {
                                var quaterData = fiscalData.filter(function (item) { return item.year == quarterNumber_4.year - 1; });
                                console.log(quaterData, fiscalData, "quaterData");
                                if (quaterData && quaterData.length) {
                                    // let startDate = new Date(quaterData[0].month_start_date);
                                    var startDate1 = getDateInBothTimeZones(quaterData[0].month_start_date.split("T")[0]);
                                    // let endDate = new Date(
                                    //   quaterData[quaterData.length - 1].month_end_date
                                    // );
                                    var endDate1 = getDateInBothTimeZones(quaterData[quaterData.length - 1].month_end_date.split("T")[0]);
                                    startDate_1 = startDate1;
                                    endDate_1 = endDate1;
                                }
                                break;
                            }
                        }
                    default:
                        break;
                }
            }
            if (startDate_1 && endDate_1) {
                ranges.push({
                    label: preset.label,
                    getRange: function () { return [startOfDay(startDate_1 !== null && startDate_1 !== void 0 ? startDate_1 : new Date()), startOfDay(endDate_1 !== null && endDate_1 !== void 0 ? endDate_1 : new Date())]; },
                });
            }
        };
        for (var _i = 0, datePresetInfo_1 = datePresetInfo; _i < datePresetInfo_1.length; _i++) {
            var preset = datePresetInfo_1[_i];
            _loop_1(preset);
        }
        return ranges;
    };
    // Generate quick ranges based on whether custom presets are provided
    var quickRanges = React.useMemo(function () {
        console.log("quickRanges Changed", datePresetInfo, datePresetVar, yearType);
        if (datePresetInfo && datePresetVar) {
            return generateQuickRanges(datePresetInfo, datePresetVar, yearType);
        }
        // Fall back to default ranges if no custom presets
        return [
            {
                label: 'Today',
                getRange: function () {
                    var today = startOfDay(new Date());
                    return [today, today];
                },
            },
            {
                label: 'Yesterday',
                getRange: function () {
                    var yesterday = startOfDay(addDays1(new Date(), -1));
                    return [yesterday, yesterday];
                },
            },
            {
                label: 'This week (Sun - Today)',
                getRange: function () {
                    var today = new Date();
                    var sunday = startOfWeek1(today, 0);
                    return [sunday, startOfDay(today)];
                },
                subOptions: [
                    {
                        label: 'Sun - Today',
                        getRange: function () {
                            var today = new Date();
                            return [startOfWeek1(today, 0), startOfDay(today)];
                        },
                    },
                    {
                        label: 'Mon - Today',
                        getRange: function () {
                            var today = new Date();
                            return [startOfWeek1(today, 1), startOfDay(today)];
                        },
                    },
                    {
                        label: 'Sat - Today',
                        getRange: function () {
                            var today = new Date();
                            return [startOfWeek1(today, 6), startOfDay(today)];
                        },
                    },
                ],
            },
            {
                label: 'This week (Mon - Today)',
                getRange: function () {
                    var today = new Date();
                    var monday = startOfWeek1(today, 1);
                    return [monday, startOfDay(today)];
                },
            },
            {
                label: 'This week (Sat - Today)',
                getRange: function () {
                    var today = new Date();
                    var saturday = startOfWeek1(today, 6);
                    return [saturday, startOfDay(today)];
                },
            },
            {
                label: 'Last week (Sun - Sat)',
                getRange: function () {
                    var today = new Date();
                    var lastWeekEnd = startOfWeek1(today, 0); // this week's Sunday
                    var lastSunday = addDays1(lastWeekEnd, -7);
                    return [lastSunday, addDays1(lastSunday, 6)];
                },
                subOptions: [
                    {
                        label: 'Sun - Sat',
                        getRange: function () {
                            var today = new Date();
                            var end = addDays1(startOfWeek1(today, 0), -1); // last week's Saturday
                            var start = addDays1(end, -6);
                            return [start, end];
                        },
                    },
                    {
                        label: 'Mon - Sun',
                        getRange: function () {
                            var today = new Date();
                            var end = addDays1(startOfWeek1(today, 1), -1); // last week's Sunday
                            var start = addDays1(end, -6);
                            return [start, end];
                        },
                    },
                    {
                        label: 'Sat - Fri',
                        getRange: function () {
                            var today = new Date();
                            var end = addDays1(startOfWeek1(today, 6), -1); // last week's Friday
                            var start = addDays1(end, -6);
                            return [start, end];
                        },
                    },
                ],
            },
            {
                label: 'Last week (Mon - Sun)',
                getRange: function () {
                    var today = new Date();
                    var lastWeekEnd = addDays1(startOfWeek1(today, 1), -1); // last week's Sunday
                    var lastMonday = addDays1(lastWeekEnd, -6);
                    return [lastMonday, lastWeekEnd];
                },
            },
            {
                label: 'Last week (Sat - Fri)',
                getRange: function () {
                    var today = new Date();
                    var lastWeekEnd = addDays1(startOfWeek1(today, 6), -1); // last week's Friday
                    var lastSaturday = addDays1(lastWeekEnd, -6);
                    return [lastSaturday, lastWeekEnd];
                },
            },
            {
                label: 'Last 7 days',
                getRange: function () {
                    var end = startOfDay(new Date());
                    var start = addDays1(end, -6);
                    return [start, end];
                },
            },
            {
                label: 'Last 28 days',
                getRange: function () {
                    var end = startOfDay(new Date());
                    var start = addDays1(end, -27);
                    return [start, end];
                },
            },
            {
                label: 'Last 30 days',
                getRange: function () {
                    var end = startOfDay(new Date());
                    var start = addDays1(end, -29);
                    return [start, end];
                },
            },
            {
                label: 'Last 90 days',
                getRange: function () {
                    var end = startOfDay(new Date());
                    var start = addDays1(end, -89);
                    return [start, end];
                },
            },
            {
                label: 'Last 12 months',
                getRange: function () {
                    var end = startOfDay(new Date());
                    var start = new Date(end);
                    start.setFullYear(end.getFullYear() - 1);
                    start.setDate(start.getDate() + 1); // include today
                    return [start, end];
                },
            },
            {
                label: 'Last calendar year',
                getRange: function () {
                    var today = new Date();
                    var lastYear = today.getFullYear() - 1;
                    return [new Date(lastYear, 0, 1), new Date(lastYear, 11, 31)];
                },
            },
            {
                label: 'This year (Jan - Today)',
                getRange: function () {
                    var today = new Date();
                    return [new Date(today.getFullYear(), 0, 1), startOfDay(today)];
                },
            },
        ];
    }, [datePresetInfo, datePresetVar, yearType]);
    // Determine initial range based on defaultDatePreset or fall back to "Last week (starts Sunday)"
    var _p = React.useMemo(function () {
        var getDefaultRangeItem = function () {
            // First check if defaultDatePreset is provided
            if (defaultDatePreset && quickRanges.length > 0) {
                // handleCheckifFiscalExistOnPresetClick();
                var presetItem = quickRanges.find(function (q) { return q.label === defaultDatePreset && q.getRange; });
                if (presetItem)
                    return presetItem;
            }
            // Fall back to "Last week (starts Sunday)" or similar
            var fallbackItem = quickRanges.find(function (q) {
                return (q.label === 'Last week (Sun - Sat)' || q.label === 'Last week (starts Sunday)') && q.getRange;
            });
            return fallbackItem || quickRanges[0];
        };
        var rangeItem = getDefaultRangeItem();
        var quickRange = (rangeItem === null || rangeItem === void 0 ? void 0 : rangeItem.getRange) ? rangeItem.getRange() : [new Date(), new Date()];
        // Set activeQuick based on defaultDatePreset or fall back
        var getDefaultActiveQuick = function () {
            if (initalLoad === true) {
                return selectedDatePreset;
            }
            ;
            if (defaultDatePreset && quickRanges.length > 0) {
                var presetExists = quickRanges.find(function (q) { return q.label === defaultDatePreset; });
                if (presetExists)
                    return defaultDatePreset;
            }
            // Fall back to the actual label from the range item
            var fallbackItem = quickRanges.find(function (q) {
                return (q.label === 'Last week (Sun - Sat)' || q.label === 'Last week (starts Sunday)') && q.getRange;
            });
            return (fallbackItem === null || fallbackItem === void 0 ? void 0 : fallbackItem.label) || 'Last 30 days';
        };
        return {
            defaultRangeItem: rangeItem,
            defaultQuickRange: quickRange,
            defaultActiveQuick: getDefaultActiveQuick()
        };
    }, [quickRanges, defaultDatePreset]), defaultRangeItem = _p.defaultRangeItem, defaultQuickRange = _p.defaultQuickRange, defaultActiveQuick = _p.defaultActiveQuick; // Use length instead of whole array to prevent unnecessary recalculations
    // When creating default range ensure we convert to native Date if props are supplied
    var initialSanitizedRange = initialStartDate !== undefined && initialEndDate !== undefined
        ? [toDate(initialStartDate) || new Date(), toDate(initialEndDate) || new Date()]
        : null;
    var _q = useState(initialSanitizedRange !== null && initialSanitizedRange !== void 0 ? initialSanitizedRange : defaultQuickRange), range = _q[0], setRange = _q[1];
    var _r = useState(defaultActiveQuick), activeQuick = _r[0], setActiveQuick = _r[1];
    var _s = useState(null), openSubMenu = _s[0], setOpenSubMenu = _s[1];
    var _t = useState(false), isOpen = _t[0], setIsOpen = _t[1];
    var _u = useState(false), isCalendarReady = _u[0], setIsCalendarReady = _u[1];
    var _v = useState(false), isCompareExpanded = _v[0], setIsCompareExpanded = _v[1];
    // Compare state
    var _w = useState(forceCompareOn || (initialCompareStartDate !== undefined && initialCompareEndDate !== undefined)), isCompareOn = _w[0], setIsCompareOn = _w[1];
    // Ensure initial compare mode is not disabled
    var getValidInitialCompareMode = function () {
        if (disabledCompareModes.includes(initialCompareMode)) {
            // Find first available mode that's not disabled
            var availableMode = compareOptions.find(function (option) { return !disabledCompareModes.includes(option.id); });
            return (availableMode === null || availableMode === void 0 ? void 0 : availableMode.id) || 'preceding';
        }
        return initialCompareMode;
    };
    var _x = useState(getValidInitialCompareMode()), compareMode = _x[0], setCompareMode = _x[1];
    var _y = useState('primary'), editingRange = _y[0], setEditingRange = _y[1];
    var _z = useState(initialCompareStartDate && initialCompareEndDate
        ? [toDate(initialCompareStartDate) || new Date(), toDate(initialCompareEndDate) || new Date()]
        : null), compareRange = _z[0], setCompareRange = _z[1];
    // Local input strings so user can freely type
    var _0 = useState(formatDisplayDate(range[0])), startInput = _0[0], setStartInput = _0[1];
    var _1 = useState(formatDisplayDate(range[1])), endInput = _1[0], setEndInput = _1[1];
    var _2 = useState(''), compareStartInput = _2[0], setCompareStartInput = _2[1];
    var _3 = useState(''), compareEndInput = _3[0], setCompareEndInput = _3[1];
    // State to track which input should have darker border (indicating next input needed)
    var _4 = useState(null), highlightedInput = _4[0], setHighlightedInput = _4[1];
    var scrollContainerRef = useRef(null);
    var lastProcessedValueRef = useRef(null);
    var comparisonSelectionTimeoutRef = useRef(null);
    var lastScrollTimeRef = useRef(0);
    var scrollAnimationRef = useRef(null);
    // Refs for input elements to handle focus
    var startInputRef = useRef(null);
    var endInputRef = useRef(null);
    var compareStartInputRef = useRef(null);
    var compareEndInputRef = useRef(null);
    // Throttled smooth scroll function to prevent rapid scroll conflicts
    var throttledScroll = useCallback(function (container, deltaY) {
        var now = Date.now();
        var timeSinceLastScroll = now - lastScrollTimeRef.current;
        // Cancel any pending animation
        if (scrollAnimationRef.current) {
            cancelAnimationFrame(scrollAnimationRef.current);
        }
        // Throttle to prevent too many rapid updates
        if (timeSinceLastScroll < 16) { // ~60fps
            return;
        }
        lastScrollTimeRef.current = now;
        scrollAnimationRef.current = requestAnimationFrame(function () {
            var scrollTop = container.scrollTop, scrollHeight = container.scrollHeight, clientHeight = container.clientHeight;
            var maxScroll = scrollHeight - clientHeight;
            // Use a smaller, consistent multiplier for smooth scrolling
            var scrollAmount = deltaY * 0.2;
            var newScrollTop = Math.max(0, Math.min(maxScroll, scrollTop + scrollAmount));
            // Direct assignment is faster than scrollTo for rapid updates
            container.scrollTop = newScrollTop;
            scrollAnimationRef.current = null;
        });
    }, []);
    // Cleanup scroll animations on unmount
    useEffect(function () {
        return function () {
            if (scrollAnimationRef.current) {
                cancelAnimationFrame(scrollAnimationRef.current);
            }
        };
    }, []);
    // Handle month name click for full month selection
    var handleMonthNameClick = useCallback(function (monthDate) {
        var startOfMonthDate = startOfDay(new Date(monthDate.getFullYear(), monthDate.getMonth(), 1));
        var endOfMonthDate = startOfDay(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0));
        var monthRange = [startOfMonthDate, endOfMonthDate];
        if (isCompareOn && compareMode === 'custom' && editingRange === 'comparison') {
            setCompareRange(monthRange);
            setEditingRange('primary');
        }
        else {
            setRange(monthRange);
            setActiveQuick('Custom');
            if (isCompareOn && compareMode === 'custom') {
                setEditingRange('comparison');
            }
        }
        // Reset selection state
        setSelectionState(resetSelectionState('primary'));
        console.log('Month selected:', monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }), 'Range:', monthRange);
    }, [isCompareOn, compareMode, editingRange]);
    // Use refs to track calendar containers and attach direct event listeners
    var calendarContainerRefs = useRef(new Map());
    // Handle calendar container clicks to detect month name clicks
    var handleCalendarClick = useCallback(function (event) {
        console.log('🔍 handleCalendarClick called!', event.target);
        var target = event.target;
        // Check if clicked element is a navigation label (month name)
        var navigationButton = target.closest('.react-calendar__navigation');
        if (navigationButton) {
            // Prevent the default calendar navigation
            event.preventDefault();
            event.stopPropagation();
            // Find the calendar container to extract the month date
            var calendarContainer = target.closest('[data-month-date]');
            if (calendarContainer) {
                var monthDateStr = calendarContainer.getAttribute('data-month-date');
                if (monthDateStr) {
                    var monthDate = new Date(monthDateStr);
                    if (!isNaN(monthDate.getTime())) {
                        handleMonthNameClick(monthDate);
                    }
                }
            }
            return;
        }
    }, [handleMonthNameClick]);
    // Function to set up navigation click listeners for a calendar container
    var setupNavigationListeners = useCallback(function (containerElement, monthDate) {
        if (!containerElement)
            return;
        // Find all navigation labels in this container
        var navigationLabels = containerElement.querySelectorAll('.react-calendar__navigation__label');
        navigationLabels.forEach(function (label) {
            // Clean up any existing listener first
            if (label._cleanupNavigationListener) {
                label._cleanupNavigationListener();
            }
            // IMPORTANT: Enable the disabled navigation button
            var buttonElement = label;
            if (buttonElement.disabled) {
                buttonElement.disabled = false;
                console.log('✅ Enabled disabled navigation button for', monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }));
            }
            // Add new listener with capture=true to intercept before React Calendar's handlers
            var handleNavigationClick = function (event) {
                console.log('🎯 Direct navigation click intercepted!', monthDate);
                event.preventDefault();
                event.stopPropagation();
                handleMonthNameClick(monthDate);
            };
            label.addEventListener('click', handleNavigationClick, { capture: true });
            // Store cleanup function
            var cleanup = function () {
                label.removeEventListener('click', handleNavigationClick, { capture: true });
            };
            // Store cleanup in the element for later removal
            label._cleanupNavigationListener = cleanup;
        });
    }, [handleMonthNameClick]);
    // Cleanup function for navigation listeners
    var cleanupNavigationListeners = useCallback(function (containerElement) {
        if (!containerElement)
            return;
        var navigationLabels = containerElement.querySelectorAll('.react-calendar__navigation__label');
        navigationLabels.forEach(function (label) {
            if (label._cleanupNavigationListener) {
                label._cleanupNavigationListener();
                delete label._cleanupNavigationListener;
            }
        });
    }, []);
    // Handle hover for selection preview
    var handleCalendarMouseMove = useCallback(function (event) {
        var _a;
        if (!selectionState.isSelecting)
            return;
        var target = event.target;
        // Find the closest calendar tile - try multiple approaches
        var tile = target.closest('.react-calendar__tile');
        // If not found, try checking if target itself is a tile
        if (!tile && target.classList.contains('react-calendar__tile')) {
            tile = target;
        }
        // If still not found, try checking parent elements
        if (!tile) {
            var parent_1 = target.parentElement;
            while (parent_1 && !parent_1.classList.contains('react-calendar__tile')) {
                parent_1 = parent_1.parentElement;
            }
            tile = parent_1;
        }
        if (!tile || tile.classList.contains('react-calendar__tile--neighboringMonth')) {
            console.log('No valid tile found or neighboring month');
            return;
        }
        // Try multiple methods to extract date
        var dateStr = '';
        // Method 1: abbr element aria-label
        var abbrElement = tile.querySelector('abbr');
        if (abbrElement) {
            dateStr = abbrElement.getAttribute('aria-label') || '';
        }
        // Method 2: button aria-label
        if (!dateStr && tile.getAttribute('aria-label')) {
            dateStr = tile.getAttribute('aria-label') || '';
        }
        // Method 3: text content as fallback
        if (!dateStr) {
            var textContent = (_a = tile.textContent) === null || _a === void 0 ? void 0 : _a.trim();
            if (textContent && /^\d+$/.test(textContent)) {
                // If it's just a number, we need more context (month/year)
                // This is more complex, but let's try the other methods first
                console.log('Only found day number:', textContent);
            }
        }
        if (dateStr) {
            var parsedDate = new Date(dateStr);
            if (!isNaN(parsedDate.getTime())) {
                var normalizedDate_1 = startOfDay(parsedDate);
                // Only update if it's different from current hover date
                if (!selectionState.hoverDate || !isSameDay(normalizedDate_1, selectionState.hoverDate)) {
                    setSelectionState(function (prev) { return (__assign(__assign({}, prev), { hoverDate: normalizedDate_1 })); });
                }
            }
            else {
                console.log('❌ Failed to parse date:', dateStr);
            }
        }
        else {
            console.log('❌ No date string found for tile:', tile);
        }
    }, [selectionState.isSelecting, selectionState.startDate, selectionState.hoverDate]);
    var handleCalendarMouseLeave = useCallback(function () {
        if (selectionState.isSelecting) {
            setSelectionState(function (prev) { return (__assign(__assign({}, prev), { hoverDate: null })); });
        }
    }, [selectionState.isSelecting]);
    // Note: Direct wheel handlers are now attached to individual containers
    // This provides better performance and eliminates flickering
    // Memoize calendar data to prevent unnecessary recalculations
    var calendarData = React.useMemo(function () {
        var today = new Date();
        var startDate = new Date(2015, 0, 1);
        var cappedMax = maxDate ? startOfDay(maxDate) : null;
        var endDate = cappedMax
            ? new Date(cappedMax.getFullYear(), cappedMax.getMonth(), cappedMax.getDate())
            : new Date(today.getFullYear() + 1, 11, 31);
        var monthCount = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth()) + 1;
        return { today: today, startDate: startDate, endDate: endDate, monthCount: monthCount, maxDate: cappedMax };
    }, [maxDate]);
    // Memoize the virtualizer to prevent re-initialization
    var virtualizer = useVirtualizer({
        count: calendarData.monthCount,
        getScrollElement: function () { return scrollContainerRef.current; },
        estimateSize: function () { return 300; }, // Includes day-grid bottom padding + month header gap
        overscan: 10, // Increased overscan for smoother scrolling
        measureElement: function (element) {
            var _a;
            // Use actual measured height for better accuracy
            return (_a = element === null || element === void 0 ? void 0 : element.getBoundingClientRect().height) !== null && _a !== void 0 ? _a : 280;
        },
    });
    // Effect to set up navigation listeners when calendar is ready and containers are mounted
    useEffect(function () {
        if (!isCalendarReady)
            return;
        // Small delay to ensure Calendar components have fully rendered
        var timeoutId = setTimeout(function () {
            var containerMap = calendarContainerRefs.current;
            containerMap.forEach(function (containerElement, index) {
                if (containerElement) {
                    var monthDate = new Date(calendarData.startDate);
                    monthDate.setMonth(monthDate.getMonth() + index);
                    setupNavigationListeners(containerElement, monthDate);
                }
            });
        }, 100);
        return function () {
            clearTimeout(timeoutId);
            // Cleanup all listeners when effect unmounts
            var containerMap = calendarContainerRefs.current;
            containerMap.forEach(function (containerElement) {
                if (containerElement) {
                    cleanupNavigationListeners(containerElement);
                }
            });
        };
    }, [isCalendarReady, setupNavigationListeners, cleanupNavigationListeners, calendarData.startDate]);
    // Effect to re-attach listeners when virtual items change (e.g., scrolling)
    useEffect(function () {
        if (!isCalendarReady)
            return;
        var timeoutId = setTimeout(function () {
            var containerMap = calendarContainerRefs.current;
            var virtualItems = virtualizer.getVirtualItems();
            virtualItems.forEach(function (virtualItem) {
                var containerElement = containerMap.get(virtualItem.index);
                if (containerElement) {
                    var monthDate = new Date(calendarData.startDate);
                    monthDate.setMonth(monthDate.getMonth() + virtualItem.index);
                    setupNavigationListeners(containerElement, monthDate);
                }
            });
        }, 150);
        return function () { return clearTimeout(timeoutId); };
    }, [virtualizer.getVirtualItems(), isCalendarReady, setupNavigationListeners, calendarData.startDate]);
    // Cleanup on unmount
    useEffect(function () {
        return function () {
            var containerMap = calendarContainerRefs.current;
            containerMap.forEach(function (containerElement) {
                if (containerElement) {
                    cleanupNavigationListeners(containerElement);
                }
            });
            containerMap.clear();
        };
    }, [cleanupNavigationListeners]);
    // Scroll to the calendar month that contains the currently selected **start date** when the popover opens.
    // We retry a few times using requestAnimationFrame to ensure the calendar grids are mounted before scrolling.
    useLayoutEffect(function () {
        if (!isOpen || !isCalendarReady)
            return;
        var targetDate = range[0];
        var targetIndex = (targetDate.getFullYear() - calendarData.startDate.getFullYear()) * 12 +
            (targetDate.getMonth() - calendarData.startDate.getMonth());
        console.log(targetIndex, "initial scroll to index", targetDate.toISOString());
        // Use virtualizer's built-in scrollToIndex method for accurate positioning
        virtualizer.scrollToIndex(targetIndex, {
            align: 'start'
        });
        // Reset selection state when opening
        setSelectionState(resetSelectionState('primary'));
        var timeoutId = setTimeout(function () {
            requestAnimationFrame(function () {
                virtualizer.scrollToIndex(targetIndex, {
                    align: 'start'
                });
            });
        }, 100);
        return function () { return clearTimeout(timeoutId); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, isCalendarReady]);
    // Keep text inputs in sync whenever ranges change
    useEffect(function () {
        setStartInput(formatDisplayDate(range[0]));
        setEndInput(formatDisplayDate(range[1]));
    }, [range]);
    // NEW: keep internal range in sync if parent updates the initial props
    useEffect(function () {
        if (initialStartDate !== undefined && initialEndDate !== undefined) {
            setRange(sanitizeRange([initialStartDate, initialEndDate]));
        }
    }, [initialStartDate, initialEndDate]);
    // Ref to track if we've already applied the default preset
    var hasAppliedDefaultRef = useRef(false);
    var lastDefaultPresetRef = useRef(null);
    // Auto-apply default preset if provided and component is mounted
    useEffect(function () {
        if (initalLoad === true)
            return;
        if (defaultDatePreset && onApply && quickRanges.length > 0) {
            // Only apply if it's a new preset or first time
            if (!hasAppliedDefaultRef.current || lastDefaultPresetRef.current !== defaultDatePreset) {
                // Try exact match first
                var presetItem = quickRanges.find(function (q) { return q.label === defaultDatePreset && q.getRange; });
                // If no exact match, try case-insensitive match
                if (!presetItem) {
                    presetItem = quickRanges.find(function (q) {
                        return q.label.toLowerCase() === defaultDatePreset.toLowerCase() && q.getRange;
                    });
                }
                if (presetItem && presetItem.getRange) {
                    var presetRange = presetItem.getRange();
                    // Auto-apply the default preset
                    onApply(presetRange[0], presetRange[1], presetItem.label);
                    hasAppliedDefaultRef.current = true;
                    lastDefaultPresetRef.current = defaultDatePreset;
                    console.log("Auto-applied preset:", presetItem.label);
                }
                else {
                    console.log("Could not auto-apply preset:", defaultDatePreset, "Available:", quickRanges.map(function (q) { return q.label; }));
                }
            }
        }
    }, [defaultDatePreset]);
    // Update range and activeQuick when defaultDatePreset changes (only once)
    useEffect(function () {
        console.log("defaultDatePreset Changed", defaultDatePreset, initalLoad, activeQuick, quickRanges);
        if (initalLoad === true)
            return;
        if (quickRanges.length > 0) {
            console.log("Available quickRanges labels:", quickRanges.map(function (q) { return q.label; }));
            console.log("Looking for defaultDatePreset:", defaultDatePreset);
            var presetItem = void 0;
            if (defaultDatePreset) {
                // Try exact match first
                presetItem = quickRanges.find(function (q) { return q.label === defaultDatePreset && q.getRange; });
                console.log("Exact match found:", presetItem === null || presetItem === void 0 ? void 0 : presetItem.label);
                // If no exact match, try case-insensitive match
                if (!presetItem) {
                    presetItem = quickRanges.find(function (q) {
                        return q.label.toLowerCase() === defaultDatePreset.toLowerCase() && q.getRange;
                    });
                    console.log("Case-insensitive match found:", presetItem === null || presetItem === void 0 ? void 0 : presetItem.label);
                }
            }
            // Fall back to "Last week" options
            if (!presetItem) {
                presetItem = quickRanges.find(function (q) {
                    return (q.label === 'Last Week (Sun - Sat)' ||
                        q.label === 'Last Week (starts Sunday)' ||
                        q.label.includes('Last Week')) && q.getRange;
                });
                console.log("Fallback match found:", presetItem === null || presetItem === void 0 ? void 0 : presetItem.label);
            }
            console.log("Final presetItem:", presetItem);
            if (presetItem && presetItem.getRange) {
                var presetRange = presetItem.getRange();
                setRange(presetRange);
                setActiveQuick(presetItem.label);
                console.log("Applied preset:", presetItem.label, "Range:", presetRange);
            }
            else {
                console.log("No valid preset found, using current range");
            }
        }
    }, [defaultDatePreset]); // Only depend on these specific values
    // NEW: Update range when yearType changes if active preset has fiscalEffect
    useEffect(function () {
        console.log(activeQuick, "activeQuick");
        if (datePresetInfo && datePresetVar && activeQuick !== 'Custom') {
            // Find the currently active preset
            var activePreset = datePresetInfo.find(function (preset) { return preset.label === activeQuick; });
            console.log(activePreset, "activePreset");
            if (activePreset && (activePreset.fiscalEffect === "TRUE" || activePreset.fiscalEffect === true)) {
                // Use the already memoized quickRanges to find the updated range
                var newActiveRange = quickRanges.find(function (range) { return range.label === activeQuick; });
                if (newActiveRange && newActiveRange.getRange) {
                    var newRange = newActiveRange.getRange();
                    // setRange(newRange);
                    var preset = datePresetInfo === null || datePresetInfo === void 0 ? void 0 : datePresetInfo.find(function (item) { return item.label === activeQuick; });
                    ;
                    if (preset && preset.fiscalEffect && preset.fiscalEffect === "TRUE") {
                        var curDate_1 = new Date();
                        var quarterNumber = fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.find(function (item) {
                            var weekStartDate = new Date(item.week_start_date);
                            var weekEndDate = new Date(item.week_end_date);
                            return curDate_1 >= weekStartDate && curDate_1 <= weekEndDate;
                        });
                        console.log(quarterNumber, "quarterNumberInside", fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length, yearType);
                        // If no quarter found and we're in fiscal mode with fiscal data, show toast and prevent selection
                        if (!quarterNumber && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) && yearType === 'fiscal') {
                            console.log("Toastify called quarterNumberInside");
                            Toastify("info", "Fiscal calendar for this date preset doesn't exist, please choose a valid preset or <br /><b>Click here to upload the fiscal calendar for the current brand</b>.", 'top-right', function () {
                                window.open("https://forms.clickup.com/9011066952/f/8chky28-11731/7B9KQQ17LUQBPC721A", "_blank");
                            });
                            // Set preset to Custom but keep the current date range
                            // setActiveQuick('Custom');
                            console.log("range", range);
                            if (onApply) {
                                setActiveQuick('Custom');
                                onApply(range[0], range[1], 'Custom', undefined, undefined, undefined);
                            }
                            console.log("Set preset to Custom due to missing fiscal calendar");
                        }
                        else {
                            if (onApply) {
                                onApply(newRange[0], newRange[1], newActiveRange.label);
                            }
                        }
                    }
                    else {
                        if (onApply) {
                            onApply(newRange[0], newRange[1], newActiveRange.label);
                        }
                    }
                    // Call onApply directly with the new range to update parent
                }
            }
        }
    }, [yearType]);
    useEffect(function () {
        setCompareStartInput(compareRange ? formatDisplayDate(compareRange[0]) : '');
        setCompareEndInput(compareRange ? formatDisplayDate(compareRange[1]) : '');
    }, [compareRange]);
    // Auto-calculate compare range based on primary range and compare mode
    useEffect(function () {
        if (!isCompareOn || compareMode === 'custom') {
            return;
        }
        var newCompareRange = null;
        var start = range[0], end = range[1];
        var dayCount = Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / (1000 * 60 * 60 * 24)) + 1;
        if (compareMode === 'preceding') {
            var compareEnd = addDays1(start, -1);
            var compareStart = addDays1(start, -dayCount);
            newCompareRange = [startOfDay(compareStart), startOfDay(compareEnd)];
        }
        else if (compareMode === 'precedingMatchDay') {
            // Match day of week for preceding period
            var startDayOfWeek = start.getDay();
            var endDayOfWeek = end.getDay();
            // Find the most recent occurrence of the start day of week before the start date
            var compareStart = new Date(start);
            var daysToSubtract = (compareStart.getDay() - startDayOfWeek + 7) % 7;
            if (daysToSubtract === 0)
                daysToSubtract = 7; // If same day, go back one week
            compareStart.setDate(compareStart.getDate() - daysToSubtract);
            // Find the most recent occurrence of the end day of week before the end date
            var compareEnd = new Date(end);
            var daysToSubtractEnd = (compareEnd.getDay() - endDayOfWeek + 7) % 7;
            if (daysToSubtractEnd === 0)
                daysToSubtractEnd = 7; // If same day, go back one week
            compareEnd.setDate(compareEnd.getDate() - daysToSubtractEnd);
            newCompareRange = [startOfDay(compareStart), startOfDay(compareEnd)];
        }
        else if (compareMode === 'lastYear') {
            var compareStart = new Date(start);
            compareStart.setFullYear(compareStart.getFullYear() - 1);
            var compareEnd = new Date(end);
            compareEnd.setFullYear(compareEnd.getFullYear() - 1);
            newCompareRange = [startOfDay(compareStart), startOfDay(compareEnd)];
        }
        else if (compareMode === 'lastYearMatchDay') {
            // Match day of week for last year
            var startDayOfWeek = start.getDay();
            var endDayOfWeek = end.getDay();
            // Find the same day of week from last year for the start date
            var compareStart = new Date(start);
            compareStart.setFullYear(compareStart.getFullYear() - 1);
            // Adjust to match the day of week
            var currentStartDayOfWeek = compareStart.getDay();
            var daysToAdjust = (startDayOfWeek - currentStartDayOfWeek + 7) % 7;
            compareStart.setDate(compareStart.getDate() + daysToAdjust);
            // Find the same day of week from last year for the end date
            var compareEnd = new Date(end);
            compareEnd.setFullYear(compareEnd.getFullYear() - 1);
            // Adjust to match the day of week
            var currentEndDayOfWeek = compareEnd.getDay();
            var daysToAdjustEnd = (endDayOfWeek - currentEndDayOfWeek + 7) % 7;
            compareEnd.setDate(compareEnd.getDate() + daysToAdjustEnd);
            newCompareRange = [startOfDay(compareStart), startOfDay(compareEnd)];
        }
        else if (compareMode === 'same' && compareRange) {
            // In 'same' mode, recalculate end date when primary range changes
            // Keep the comparison start date but update the end date based on new duration
            var compareStart = compareRange[0];
            var compareEnd = addDays1(compareStart, dayCount - 1);
            newCompareRange = [startOfDay(compareStart), startOfDay(compareEnd)];
        }
        if (newCompareRange) {
            setCompareRange(newCompareRange);
        }
    }, [range, compareMode, isCompareOn]);
    // Handle mode switching for 'same' mode
    useEffect(function () {
        if (isCompareOn && compareMode === 'same' && !compareRange) {
            // When switching to 'same' mode without existing compare range, start with primary mode
            setEditingRange('primary');
        }
    }, [compareMode, isCompareOn, compareRange]);
    // Handle when current compare mode becomes disabled
    useEffect(function () {
        if (disabledCompareModes.includes(compareMode)) {
            // Find first available mode that's not disabled
            var availableMode = compareOptions.find(function (option) { return !disabledCompareModes.includes(option.id); });
            if (availableMode) {
                var newMode = availableMode.id;
                setCompareMode(newMode);
                console.log("Compare mode \"".concat(compareMode, "\" is disabled, switching to \"").concat(newMode, "\""));
            }
        }
    }, [disabledCompareModes, compareMode]);
    // Reset selection state when editing range changes
    useEffect(function () {
        setSelectionState({
            isSelecting: false,
            startDate: null,
            currentRange: null,
            editingRange: editingRange,
            hoverDate: null
        });
        // Clear highlighting when switching editing ranges
        setHighlightedInput(null);
        // Clear any comparison selection timeout
        if (comparisonSelectionTimeoutRef.current) {
            clearTimeout(comparisonSelectionTimeoutRef.current);
            comparisonSelectionTimeoutRef.current = null;
        }
    }, [editingRange]);
    var parseInputDate = function (str) {
        var parsed = new Date(str);
        return isNaN(parsed.getTime()) ? null : startOfDay(parsed);
    };
    var commitStartInput = function () {
        var parsed = parseInputDate(startInput);
        if (parsed) {
            var newStart_1 = calendarData.maxDate && parsed > calendarData.maxDate ? calendarData.maxDate : parsed;
            var newEnd = range[1];
            if (calendarData.maxDate && newEnd > calendarData.maxDate)
                newEnd = calendarData.maxDate;
            if (newStart_1 > newEnd)
                newEnd = newStart_1;
            setRange([newStart_1, newEnd]);
            setActiveQuick('Custom');
            // Highlight the end date input if end date equals start date (indicating incomplete selection)
            if (newStart_1.getTime() === newEnd.getTime()) {
                setHighlightedInput('end');
            }
            else {
                setHighlightedInput(null);
            }
            // Scroll to the start month when date is manually entered
            if (isCalendarReady) {
                setTimeout(function () {
                    scrollToMonth(newStart_1);
                }, 100);
            }
        }
        else {
            setStartInput(formatDisplayDate(range[0]));
        }
    };
    var commitEndInput = function () {
        var parsed = parseInputDate(endInput);
        if (parsed) {
            var newEnd = calendarData.maxDate && parsed > calendarData.maxDate ? calendarData.maxDate : parsed;
            var newStart_2 = range[0];
            if (newEnd < newStart_2)
                newStart_2 = newEnd;
            setRange([newStart_2, newEnd]);
            setActiveQuick('Custom');
            // Clear highlighting when end date is manually entered
            setHighlightedInput(null);
            // Scroll to the start month when end date is manually entered
            if (isCalendarReady) {
                setTimeout(function () {
                    scrollToMonth(newStart_2);
                }, 100);
            }
        }
        else {
            setEndInput(formatDisplayDate(range[1]));
        }
    };
    var commitCompareStartInput = function () {
        var parsed = parseInputDate(compareStartInput);
        if (parsed && compareRange) {
            var newCompareRange = [parsed, compareRange[1]];
            setCompareRange(newCompareRange);
            // Highlight the comparison end date input if end date equals start date (indicating incomplete selection)
            if (parsed.getTime() === compareRange[1].getTime()) {
                setHighlightedInput('compareEnd');
            }
            else {
                setHighlightedInput(null);
            }
        }
        else {
            setCompareStartInput(compareRange ? formatDisplayDate(compareRange[0]) : '');
        }
    };
    var commitCompareEndInput = function () {
        var parsed = parseInputDate(compareEndInput);
        if (parsed && compareRange) {
            setCompareRange([compareRange[0], parsed]);
            // Clear highlighting when comparison end date is manually entered
            setHighlightedInput(null);
        }
        else {
            setCompareEndInput(compareRange ? formatDisplayDate(compareRange[1]) : '');
        }
    };
    // NEW: Enhanced calendar change handler that supports multi-month selection
    var handleCalendarChange = function (value) {
        // If presetOnly is enabled, prevent manual calendar selection
        if (presetOnly) {
            return;
        }
        // Handle single date clicks for multi-month selection
        if (value instanceof Date && !Array.isArray(value)) {
            var clickedDate = startOfDay(value);
            // Block dates beyond maxDate when provided
            if (calendarData.maxDate && clickedDate > calendarData.maxDate) {
                return;
            }
            // Special handling for comparison modes
            if (isCompareOn && compareMode === 'same' && editingRange === 'comparison') {
                // Calculate end date based on primary range duration
                var primaryStart = range[0], primaryEnd = range[1];
                var dayCount = Math.round((startOfDay(primaryEnd).getTime() - startOfDay(primaryStart).getTime()) / (1000 * 60 * 60 * 24)) + 1;
                var compareEnd = addDays1(clickedDate, dayCount - 1);
                setCompareRange([clickedDate, startOfDay(compareEnd)]);
                // Switch back to primary mode
                setTimeout(function () {
                    setEditingRange('primary');
                }, 50);
                return;
            }
            // Multi-month selection logic
            if (!selectionState.isSelecting) {
                // First click - start selection and store previous ranges for cancel functionality
                setSelectionState({
                    isSelecting: true,
                    startDate: clickedDate,
                    currentRange: [clickedDate, clickedDate],
                    editingRange: editingRange,
                    hoverDate: null,
                    previousRange: range,
                    previousCompareRange: compareRange
                });
                // Update the range immediately
                if (isCompareOn && compareMode === 'custom' && editingRange === 'comparison') {
                    setCompareRange([clickedDate, clickedDate]);
                    // Highlight the comparison end date input
                    setHighlightedInput('compareEnd');
                }
                else if (isCompareOn && compareMode === 'same' && editingRange === 'primary') {
                    // For 'same' mode, when selecting primary range, switch to comparison after completion
                    setRange([clickedDate, clickedDate]);
                    setActiveQuick('Custom');
                }
                else {
                    setRange([clickedDate, clickedDate]);
                    setActiveQuick('Custom');
                    // Highlight the primary end date input
                    setHighlightedInput('end');
                }
                return;
            }
            else {
                // Second click - complete selection
                var startDate_2 = selectionState.startDate;
                var endDate_2 = clickedDate;
                // Ensure proper order
                var finalRange = startDate_2 <= endDate_2
                    ? [startDate_2, endDate_2]
                    : [endDate_2, startDate_2];
                if (isCompareOn && compareMode === 'custom' && editingRange === 'comparison') {
                    setCompareRange(finalRange);
                    setEditingRange('primary');
                    // Clear highlighting when comparison range is complete
                    setHighlightedInput(null);
                }
                else if (isCompareOn && compareMode === 'same' && editingRange === 'primary') {
                    // For 'same' mode, after completing primary range, switch to comparison mode
                    setRange(finalRange);
                    setActiveQuick('Custom');
                    setEditingRange('comparison');
                }
                else {
                    setRange(finalRange);
                    setActiveQuick('Custom');
                    if (isCompareOn && compareMode === 'custom') {
                        setEditingRange('comparison');
                    }
                    // Clear highlighting when primary range is complete
                    setHighlightedInput(null);
                }
                // Reset selection state
                setSelectionState(resetSelectionState(editingRange));
                return;
            }
        }
        // Handle range selection from react-calendar (legacy behavior for single month selections)
        if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
            var newRange = [startOfDay(value[0]), startOfDay(value[1])];
            // Reset selection state since we got a complete range
            setSelectionState({
                isSelecting: false,
                startDate: null,
                currentRange: null,
                editingRange: editingRange,
                hoverDate: null
            });
            if (isCompareOn && compareMode === 'custom') {
                if (editingRange === 'comparison') {
                    setCompareRange(newRange);
                    setEditingRange('primary');
                }
                else {
                    setRange(newRange);
                    setActiveQuick('Custom');
                    setEditingRange('comparison');
                }
            }
            else {
                setRange(newRange);
                setActiveQuick('Custom');
            }
            console.log('Applied range from calendar:', newRange);
        }
    };
    // Function to scroll to a specific month
    var scrollToMonth = function (targetDate) {
        var targetIndex = (targetDate.getFullYear() - calendarData.startDate.getFullYear()) * 12 +
            (targetDate.getMonth() - calendarData.startDate.getMonth());
        console.log(targetIndex, "scrolling to index", targetDate.toISOString());
        // Use virtualizer's built-in scrollToIndex method for accurate positioning
        virtualizer.scrollToIndex(targetIndex, {
            align: 'start',
        });
    };
    var handleQuickSelect = function (label, getRange) {
        if (getRange) {
            var newRange_1 = getRange();
            console.log(newRange_1, "newRangeSelected", getRange);
            setRange(newRange_1);
            setActiveQuick(label);
            setOpenSubMenu(null);
            // Reset selection state and clear highlighting
            setSelectionState({
                isSelecting: false,
                startDate: null,
                currentRange: null,
                editingRange: 'primary',
                hoverDate: null
            });
            setHighlightedInput(null);
            // Auto-switch to comparison mode for 'same' mode after selecting a preset
            if (isCompareOn && compareMode === 'same') {
                setEditingRange('comparison');
            }
            // Scroll to the start month of the selected range
            if (isCalendarReady) {
                setTimeout(function () {
                    scrollToMonth(newRange_1[0]);
                }, 100);
            }
        }
        else {
            setOpenSubMenu(function (prev) { return (prev === label ? null : label); });
        }
    };
    var handleCompareToggle = function () {
        var nextState = !isCompareOn;
        setIsCompareOn(nextState);
        if (nextState) {
            // Trigger re-calculation of default compare range
            setCompareMode('preceding');
            // Auto-expand the compare section when turned on
            setIsCompareExpanded(true);
        }
        else {
            setCompareRange(null);
            setIsCompareExpanded(false);
        }
    };
    var handleCancel = function () {
        // If user was in the middle of selecting, restore previous ranges
        if (selectionState.isSelecting) {
            if (selectionState.previousRange) {
                setRange(selectionState.previousRange);
            }
            if (selectionState.previousCompareRange) {
                setCompareRange(selectionState.previousCompareRange);
            }
            console.log('Cancelled selection - restored previous ranges');
        }
        setIsOpen(false);
        // Reset selection state and clear highlighting
        setSelectionState(resetSelectionState('primary'));
        setHighlightedInput(null);
    };
    var handleApply = function () {
        setIsOpen(false);
        // Reset selection state and clear highlighting
        setSelectionState({
            isSelecting: false,
            startDate: null,
            currentRange: null,
            editingRange: 'primary',
            hoverDate: null
        });
        setHighlightedInput(null);
        if (onApply) {
            if (isCompareOn && compareRange) {
                onApply(range[0], range[1], activeQuick, compareRange[0], compareRange[1], compareMode);
            }
            else {
                onApply(range[0], range[1], activeQuick, undefined, undefined, undefined);
            }
        }
    };
    var handleYearTypeChange = function (newYearType) {
        if (newYearType === 'fiscal') {
            console.log("Fiscal year type changed", selectedDatePreset);
            // Find the preset in datePresetInfo
            var preset = datePresetInfo === null || datePresetInfo === void 0 ? void 0 : datePresetInfo.find(function (item) { return item.label === selectedDatePreset; });
            ;
            if (preset && preset.fiscalEffect && preset.fiscalEffect === "TRUE") {
                var curDate_2 = new Date();
                var quarterNumber = fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.find(function (item) {
                    var weekStartDate = new Date(item.week_start_date);
                    var weekEndDate = new Date(item.week_end_date);
                    return curDate_2 >= weekStartDate && curDate_2 <= weekEndDate;
                });
                console.log(quarterNumber, "quarterNumber", fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length, newYearType);
                // If no quarter found and we're in fiscal mode with fiscal data, show toast and prevent selection
                if (!quarterNumber && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) && newYearType === 'fiscal') {
                    console.log("Toastify called");
                    Toastify("info", "Fiscal calendar for this date preset doesn't exist, please choose a valid preset or <br /><b>Click here to upload the fiscal calendar for the current brand</b>.", 'top-right', function () {
                        window.open("https://forms.clickup.com/9011066952/f/8chky28-11731/7B9KQQ17LUQBPC721A", "_blank");
                    });
                    // Set preset to Custom but keep the current date range
                    // setActiveQuick('Custom');
                    console.log("range", range);
                    if (onApply) {
                        setActiveQuick('Custom');
                        onApply(range[0], range[1], 'Custom', undefined, undefined, undefined);
                    }
                    console.log("Set preset to Custom due to missing fiscal calendar");
                    // return false;
                }
            }
            // Check if fiscal data exists for this preset
        }
        if (onYearTypeChange) {
            onYearTypeChange(newYearType);
        }
    };
    var startDate = range[0], endDate = range[1];
    var handleCheckifFiscalExistOnPresetClick = function () {
        var _a;
        var curDate = new Date();
        // Find the fiscal quarter based on the current date
        var quarterNumber = fiscalData.find(function (item) {
            var weekStartDate = new Date(item.week_start_date);
            var weekEndDate = new Date(item.week_end_date);
            return curDate >= weekStartDate && curDate <= weekEndDate;
        });
        console.log(quarterNumber, "quarterNUmber", (_a = fiscalData[0]) === null || _a === void 0 ? void 0 : _a.brand_id, brandId, yearType, fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length);
        if (!quarterNumber &&
            // (fiscalData[0]?.brand_id === brandId) &&
            yearType === 'fiscal' &&
            (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length)
        // changeRange !== 0
        ) {
            console.log("Toastify called");
            Toastify("info", "Fiscal calendar for this date preset doesn't exist, please choose a valid preset or <br /><b>Click here to upload the fiscal calendar for the current brand</b>.", 'top-right', function () {
                window.open("https://forms.clickup.com/9011066952/f/8chky28-11731/7B9KQQ17LUQBPC721A", "_blank");
            });
            // return false;
        }
        // return true;
    };
    // Function to check if a preset can be selected based on fiscal data
    var canSelectPreset = function (label) {
        // If not in fiscal mode, always allow selection
        if (yearType !== 'fiscal')
            return true;
        // Find the preset in datePresetInfo
        var preset = datePresetInfo === null || datePresetInfo === void 0 ? void 0 : datePresetInfo.find(function (item) { return item.label === label; });
        if (!preset)
            return true; // If preset not found, allow selection
        // If preset doesn't have fiscal effect, allow selection
        if (!preset.fiscalEffect || preset.fiscalEffect === "FALSE")
            return true;
        // Check if fiscal data exists for this preset
        var curDate = new Date();
        var quarterNumber = fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.find(function (item) {
            var weekStartDate = new Date(item.week_start_date);
            var weekEndDate = new Date(item.week_end_date);
            return curDate >= weekStartDate && curDate <= weekEndDate;
        });
        // If no quarter found and we're in fiscal mode with fiscal data, show toast and prevent selection
        if (!quarterNumber && (fiscalData === null || fiscalData === void 0 ? void 0 : fiscalData.length) && yearType === 'fiscal') {
            handleCheckifFiscalExistOnPresetClick();
            return false;
        }
        return true;
    };
    // Year type options for the dropdown
    var yearTypeOptions = __spreadArray(__spreadArray(__spreadArray([], (fiscal ? [{ id: 'fiscal', label: 'Fiscal Year' }] : []), true), [
        { id: 'calendar', label: 'Calendar Year' }
    ], false), (showRelative ? [{ id: 'relative', label: 'Relative' }] : []), true);
    // Helper function to get the current display range for calendar value
    var getCurrentCalendarValue = function () {
        if (isCompareOn && compareMode === 'custom' && editingRange === 'comparison') {
            return compareRange;
        }
        if (isCompareOn && compareMode === 'same' && editingRange === 'comparison') {
            return compareRange;
        }
        // If we're in the middle of selecting, show the temporary range
        if (selectionState.isSelecting && selectionState.currentRange) {
            return selectionState.currentRange;
        }
        return range;
    };
    return (_jsxs("div", { className: "flex items-center gap-3", children: [(fiscal || showRelative) && (_jsx(DropdownSingleSelect, { items: yearTypeOptions, selectedItem: yearType, onSelectionChange: function (selectedId) { return handleYearTypeChange(selectedId); }, triggerLabel: '', icon: _jsx(SvgCalendar, { className: "w-4 h-4 text-gray-600 dark:text-muted-foreground" }), className: "px-2 w-fit min-w-fit rounded-lg border-none focus:ring-0 focus:ring-offset-0 shadow-none outline-none focus:outline-none focus-visible:ring-0 focus-visible:ring-offset-0" })), yearType === 'relative' ? (_jsx(DropdownSingleSelect, { items: relativeOptions.map(function (o) { return ({ id: o.id, label: o.label }); }), selectedItem: internalRelativeOption, onSelectionChange: handleRelativeSelect, triggerLabel: "", name: "Select period", icon: _jsx(CalendarIcon, { className: "w-4 h-4 text-gray-600 dark:text-muted-foreground" }), className: "min-w-[200px] w-auto justify-start text-left font-normal px-2 border rounded-md hover:bg-[#F5F5F5] dark:hover:bg-muted hover:text-[#242424] dark:hover:text-foreground" })) : (_jsxs(Popover, { open: isOpen, onOpenChange: function (open) {
                    setIsOpen(open);
                    // Prevent autofocus when opening
                    if (open) {
                        setIsCalendarReady(false);
                        setTimeout(function () {
                            if (document.activeElement && document.activeElement instanceof HTMLElement) {
                                document.activeElement.blur();
                            }
                            setIsCalendarReady(true);
                        }, 50);
                    }
                    else {
                        setIsCalendarReady(false);
                        // Reset selection state and clear highlighting when closing
                        setSelectionState({
                            isSelecting: false,
                            startDate: null,
                            currentRange: null,
                            editingRange: 'primary',
                            hoverDate: null
                        });
                        setHighlightedInput(null);
                    }
                }, children: [_jsx(PopoverTrigger, { asChild: true, children: _jsxs(Button, { variant: "outline", className: cn("min-w-[200px] w-auto justify-start text-left font-normal px-2 border-none hover:bg-[#F5F5F5] dark:hover:bg-muted hover:text-[#242424] dark:hover:text-foreground shadow-none", !range && "text-muted-foreground"), children: [_jsx(CalendarIcon, { className: "mr-0 h-4 w-4" }), range ? (_jsxs(_Fragment, { children: [formatDisplayDate(range[0]), " - ", formatDisplayDate(range[1])] })) : (_jsx("span", { children: "Pick a date range" })), _jsx(ChevronDown, { className: "h-4 w-4 ml-auto" })] }) }), _jsx(PopoverContent, { className: "z-[60] w-auto p-0", align: "end", children: _jsxs("div", { className: "flex bg-white dark:bg-background rounded-lg shadow-lg p-4 w-full space-x-6 ", style: { maxWidth: '620px' }, children: [_jsxs("div", { className: "w-[35%] pr-2 border-r border-border flex flex-col h-[400px]", children: [_jsx("div", { className: "h-[400px] overflow-y-auto scrollbar-none", style: {
                                                scrollBehavior: 'auto',
                                                overscrollBehavior: 'contain',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                            }, onWheel: function (e) {
                                                // Handle wheel events specifically for the quick ranges section
                                                e.stopPropagation();
                                                e.preventDefault();
                                                var container = e.currentTarget;
                                                var scrollTop = container.scrollTop, scrollHeight = container.scrollHeight, clientHeight = container.clientHeight;
                                                var isScrollingDown = e.deltaY > 0;
                                                var isScrollingUp = e.deltaY < 0;
                                                var canScrollDown = scrollTop < scrollHeight - clientHeight - 1;
                                                var canScrollUp = scrollTop > 1;
                                                if ((isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)) {
                                                    // Use throttled scroll to prevent glitches during fast scrolling
                                                    throttledScroll(container, e.deltaY);
                                                }
                                            }, children: _jsx("ul", { className: "space-y-0.5", children: quickRanges.map(function (item) { return (_jsxs("li", { className: "relative", children: [_jsxs("div", { className: "flex items-center px-3 py-2 rounded text-xs transition-colors cursor-pointer ".concat(activeQuick === item.label ? 'bg-primary-light text-[hsl(var(--primary-pressed-dark))] dark:text-primary font-medium' : 'text-gray-700 dark:text-foreground hover:bg-gray-100 dark:hover:bg-muted'), onClick: function () {
                                                                var _a;
                                                                if (canSelectPreset(item.label)) {
                                                                    handleQuickSelect(item.label, (_a = item.getRange) !== null && _a !== void 0 ? _a : undefined);
                                                                }
                                                            }, children: [_jsx("span", { children: item.label }), item.subOptions && _jsx("span", { className: "ml-auto text-gray-400 dark:text-muted-foreground", children: "\u25B8" })] }), item.subOptions && openSubMenu === item.label && (_jsx("ul", { className: "absolute left-full top-0 ml-2 w-40 bg-white dark:bg-background rounded shadow-lg z-20", children: item.subOptions.map(function (sub) { return (_jsx("li", { className: "px-3 py-2 text-sm cursor-pointer whitespace-nowrap transition-colors ".concat(activeQuick === sub.label ? 'bg-primary-light text-[hsl(var(--primary-pressed-dark))] dark:text-primary font-medium' : 'text-gray-700 dark:text-foreground hover:bg-gray-100 dark:hover:bg-muted'), onClick: function () {
                                                                    if (canSelectPreset(sub.label)) {
                                                                        handleQuickSelect(sub.label, sub.getRange);
                                                                    }
                                                                }, children: sub.label }, sub.label)); }) }))] }, item.label)); }) }) }), showCompareToggle && (_jsx("div", { className: "border-t pt-2 mt-2 flex-1 flex flex-col", children: _jsxs("div", { className: "flex-1 flex flex-col", children: [_jsxs("div", { className: "flex items-center justify-between mb-1", children: [_jsxs("div", { className: "flex items-center space-x-1", children: [_jsx("span", { className: "text-xs font-medium text-gray-700 dark:text-foreground", children: "Compare" }), _jsx(TooltipProvider, { children: _jsxs(Tooltip, { children: [_jsx(TooltipTrigger, { asChild: true, children: _jsx(SvgInfoCircle, { className: "w-4 h-4 text-gray-700 dark:text-foreground hover:text-gray-600 dark:hover:text-muted-foreground cursor-help" }) }), _jsx(TooltipContent, { className: "z-[60]", children: _jsx("p", { children: "Compare your selected date range with a previous period to analyze trends and changes over time. Open the compare menu to see more options." }) })] }) })] }), _jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(Switch, { checked: isCompareOn, onCheckedChange: forceCompareOn ? undefined : handleCompareToggle, disabled: forceCompareOn, size: "sm", className: "data-[state=checked]:bg-[hsl(var(--primary-pressed-dark))] data-[state=unchecked]:bg-gray-200 dark:data-[state=unchecked]:bg-muted focus-visible:ring-[hsl(var(--primary-pressed-dark))] disabled:opacity-50" }), isCompareOn && (_jsx("button", { onClick: function () { return setIsCompareExpanded(!isCompareExpanded); }, className: "p-1 hover:bg-gray-100 dark:hover:bg-muted rounded transition-colors", children: isCompareExpanded ? (_jsx(ChevronUp, { className: "w-3 h-3 text-gray-500 dark:text-muted-foreground" })) : (_jsx(ChevronDown, { className: "w-3 h-3 text-gray-500 dark:text-muted-foreground" })) }))] })] }), isCompareOn && isCompareExpanded && (_jsx("div", { className: "flex-1 overflow-y-auto scrollbar-none", style: { scrollbarWidth: 'none', msOverflowStyle: 'none' }, children: _jsx("ul", { className: "space-y-0.5", children: compareOptions.map(function (option) {
                                                                var isDisabled = disabledCompareModes.includes(option.id);
                                                                return (_jsx("li", { children: _jsxs("label", { className: "flex items-center px-3 py-2 rounded text-xs transition-colors w-full ".concat(isDisabled
                                                                            ? 'text-gray-400 dark:text-muted-foreground cursor-not-allowed bg-gray-50 dark:bg-muted'
                                                                            : compareMode === option.id
                                                                                ? 'bg-primary-light text-[hsl(var(--primary-pressed-dark))] font-medium cursor-pointer hover:bg-primary-light/80'
                                                                                : 'text-gray-700 dark:text-foreground hover:bg-gray-100 dark:hover:bg-muted cursor-pointer'), onClick: function () {
                                                                            if (!isDisabled) {
                                                                                var newMode = option.id;
                                                                                setCompareMode(newMode);
                                                                                if (newMode === 'custom') {
                                                                                    setEditingRange('primary');
                                                                                }
                                                                                else if (newMode === 'same') {
                                                                                    setEditingRange('primary'); // Start with primary range selection
                                                                                    setCompareRange(null); // Clear existing range to force new selection
                                                                                }
                                                                                // Reset selection state when changing modes
                                                                                setSelectionState({
                                                                                    isSelecting: false,
                                                                                    startDate: null,
                                                                                    currentRange: null,
                                                                                    editingRange: newMode === 'custom' ? 'primary' : 'primary',
                                                                                    hoverDate: null
                                                                                });
                                                                            }
                                                                        }, children: [_jsx("input", { type: "radio", name: "compareMode", value: option.id, checked: compareMode === option.id, disabled: isDisabled, onChange: function (e) {
                                                                                    if (!isDisabled) {
                                                                                        var newMode = e.target.value;
                                                                                        setCompareMode(newMode);
                                                                                        if (newMode === 'custom') {
                                                                                            setEditingRange('primary');
                                                                                        }
                                                                                        else if (newMode === 'same') {
                                                                                            setEditingRange('primary'); // Start with primary range selection
                                                                                            setCompareRange(null); // Clear existing range to force new selection
                                                                                        }
                                                                                        // Reset selection state when changing modes
                                                                                        setSelectionState({
                                                                                            isSelecting: false,
                                                                                            startDate: null,
                                                                                            currentRange: null,
                                                                                            editingRange: newMode === 'custom' ? 'primary' : 'primary',
                                                                                            hoverDate: null
                                                                                        });
                                                                                    }
                                                                                }, className: "sr-only" }), _jsxs("div", { className: "flex flex-col flex-1", children: [_jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { children: option.label }), isDisabled && (_jsx("span", { className: "text-xs text-orange-500 font-medium", children: disabledCompareText }))] }), option.subtext && (_jsx("span", { className: "text-xs text-gray-500 dark:text-muted-foreground font-normal", children: option.subtext }))] })] }) }, option.id));
                                                            }) }) }))] }) }))] }), _jsxs("div", { className: "w-[65%] flex flex-col min-w-0", children: [isCompareOn && (compareMode === 'custom' || compareMode === 'same') && (_jsx("div", { className: "mb-2 p-2 bg-muted rounded text-xs", children: _jsxs("div", { className: "flex items-center justify-between", children: [_jsx("span", { className: "text-muted-foreground", children: "Editing:" }), compareMode === 'custom' && (_jsxs("div", { className: "flex items-center space-x-4", children: [_jsxs("div", { className: "flex items-center space-x-1 cursor-pointer ".concat(editingRange === 'primary' ? 'text-[hsl(var(--primary-pressed-dark))] font-medium' : 'text-gray-500 dark:text-muted-foreground'), onClick: function () {
                                                                    setEditingRange('primary');
                                                                    setSelectionState({
                                                                        isSelecting: false,
                                                                        startDate: null,
                                                                        currentRange: null,
                                                                        editingRange: 'primary',
                                                                        hoverDate: null
                                                                    });
                                                                }, children: [_jsx("div", { className: "w-3 h-3 rounded ".concat(editingRange === 'primary' ? 'bg-[hsl(var(--primary-pressed-dark))]' : 'bg-gray-300 dark:bg-muted') }), _jsx("span", { children: "Primary Range" })] }), _jsxs("div", { className: "flex items-center space-x-1 cursor-pointer ".concat(editingRange === 'comparison' ? 'text-[hsl(var(--primary-pressed-dark))] font-medium' : 'text-gray-500 dark:text-muted-foreground'), onClick: function () {
                                                                    setEditingRange('comparison');
                                                                    setSelectionState({
                                                                        isSelecting: false,
                                                                        startDate: null,
                                                                        currentRange: null,
                                                                        editingRange: 'comparison',
                                                                        hoverDate: null
                                                                    });
                                                                }, children: [_jsx("div", { className: "w-3 h-3 rounded ".concat(editingRange === 'comparison' ? 'bg-[#bae7e8]' : 'bg-gray-300 dark:bg-muted') }), _jsx("span", { children: "Comparison Range" })] })] })), compareMode === 'same' && (_jsxs("div", { className: "flex items-center space-x-4", children: [_jsxs("div", { className: "flex items-center space-x-1 cursor-pointer ".concat(editingRange === 'primary' ? 'text-[hsl(var(--primary-pressed-dark))] font-medium' : 'text-gray-500 dark:text-muted-foreground'), onClick: function () {
                                                                    setEditingRange('primary');
                                                                    setSelectionState({
                                                                        isSelecting: false,
                                                                        startDate: null,
                                                                        currentRange: null,
                                                                        editingRange: 'primary',
                                                                        hoverDate: null
                                                                    });
                                                                }, children: [_jsx("div", { className: "w-3 h-3 rounded ".concat(editingRange === 'primary' ? 'bg-[hsl(var(--primary-pressed-dark))]' : 'bg-gray-300 dark:bg-muted') }), _jsx("span", { children: "Primary Range" })] }), _jsxs("div", { className: "flex items-center space-x-1 cursor-pointer ".concat(editingRange === 'comparison' ? 'text-[hsl(var(--primary-pressed-dark))] font-medium' : 'text-gray-500 dark:text-muted-foreground'), onClick: function () {
                                                                    setEditingRange('comparison');
                                                                    setSelectionState({
                                                                        isSelecting: false,
                                                                        startDate: null,
                                                                        currentRange: null,
                                                                        editingRange: 'comparison',
                                                                        hoverDate: null
                                                                    });
                                                                }, children: [_jsx("div", { className: "w-3 h-3 rounded ".concat(editingRange === 'comparison' ? 'bg-[#bae7e8]' : 'bg-gray-300 dark:bg-muted') }), _jsx("span", { children: "Select Compare Start" })] })] }))] }) })), _jsxs("div", { className: "flex items-start space-x-3 mb-1 min-w-0", children: [_jsxs("div", { className: "flex flex-col w-36 min-w-[100px]", children: [_jsx("label", { className: "text-xs text-[hsl(var(--primary-pressed-dark))] dark:text-primary mb-1", children: "Start date" }), _jsx("input", { ref: startInputRef, type: "text", value: startInput, onChange: function (e) { return setStartInput(e.target.value); }, onBlur: commitStartInput, onFocus: function (e) { return e.target.tabIndex = 0; }, placeholder: "Start date", autoFocus: false, tabIndex: -1, readOnly: presetOnly, className: "border rounded px-3 py-2 text-xs bg-background text-foreground focus:border-[hsl(var(--primary-pressed-dark))] dark:focus:border-primary focus:ring-1 focus:ring-[hsl(var(--primary-pressed-dark))] dark:focus:ring-primary outline-none truncate w-full ".concat(highlightedInput !== 'end' ? 'border-[hsl(var(--primary-pressed-dark))] dark:border-primary' : '', " ").concat(presetOnly ? 'bg-gray-50 dark:bg-muted cursor-not-allowed' : '') })] }), _jsx("span", { className: "mt-7 text-gray-600 dark:text-muted-foreground flex-shrink-0", children: "\u2014" }), _jsxs("div", { className: "flex flex-col w-36 min-w-[100px]", children: [_jsx("label", { className: "text-xs text-gray-500 dark:text-muted-foreground mb-1", children: "End date" }), _jsx("input", { ref: endInputRef, type: "text", value: endInput, onChange: function (e) { return setEndInput(e.target.value); }, onBlur: commitEndInput, onFocus: function (e) { return e.target.tabIndex = 0; }, placeholder: "End date", autoFocus: false, tabIndex: -1, readOnly: presetOnly, className: "border rounded px-3 py-2 text-xs bg-background text-foreground focus:border-[hsl(var(--primary-pressed-dark))] dark:focus:border-primary focus:ring-1 focus:ring-[hsl(var(--primary-pressed-dark))] dark:focus:ring-primary outline-none truncate w-full ".concat(highlightedInput === 'end' ? 'border-[hsl(var(--primary-pressed-dark))] dark:border-primary' : '', " ").concat(presetOnly ? 'bg-gray-50 dark:bg-muted cursor-not-allowed' : '') })] })] }), isCompareOn && (_jsxs("div", { className: "flex items-start space-x-3 mb-4 min-w-0", children: [_jsxs("div", { className: "flex flex-col w-36 min-w-[100px]", children: [_jsx("label", { className: "text-xs text-gray-500 dark:text-muted-foreground mb-1", children: "Compare: Start date" }), _jsx("input", { ref: compareStartInputRef, type: "text", value: compareStartInput, onChange: function (e) { return setCompareStartInput(e.target.value); }, onBlur: commitCompareStartInput, readOnly: presetOnly || compareMode !== 'custom', placeholder: "Compare start", autoFocus: false, tabIndex: -1, onFocus: function (e) { return e.target.tabIndex = 0; }, className: "border rounded px-3 py-2 text-xs outline-none truncate w-full ".concat(compareMode === 'custom' && !presetOnly ? 'focus:border-[hsl(var(--primary-pressed-dark))] focus:ring-1 focus:ring-[hsl(var(--primary-pressed-dark))]' : 'bg-gray-50 dark:bg-muted', " ").concat(presetOnly ? 'cursor-not-allowed' : '') })] }), _jsx("span", { className: "mt-7 text-gray-600 dark:text-muted-foreground flex-shrink-0", children: "\u2014" }), _jsxs("div", { className: "flex flex-col w-36 min-w-[100px]", children: [_jsx("label", { className: "text-xs text-gray-500 dark:text-muted-foreground mb-1", children: "End date" }), _jsx("input", { ref: compareEndInputRef, type: "text", value: compareEndInput, onChange: function (e) { return setCompareEndInput(e.target.value); }, onBlur: commitCompareEndInput, readOnly: presetOnly || compareMode !== 'custom', placeholder: "Compare end", autoFocus: false, tabIndex: -1, onFocus: function (e) { return e.target.tabIndex = 0; }, className: "border rounded px-3 py-2 text-xs outline-none truncate w-full ".concat(compareMode === 'custom' && !presetOnly ? 'focus:border-[hsl(var(--primary-pressed-dark))] focus:ring-1 focus:ring-[hsl(var(--primary-pressed-dark))]' : 'bg-gray-50 dark:bg-muted', " ").concat(highlightedInput === 'compareEnd' ? 'border-[hsl(var(--primary-pressed-dark))] border-2' : '', " ").concat(presetOnly ? 'cursor-not-allowed' : '') })] })] })), weekStartsOn !== 1 && (_jsx("style", { dangerouslySetInnerHTML: { __html: getWeekReorderStyles(weekStartsOn, 'date-range-calendar') } })), _jsx("div", { id: "date-range-calendar", ref: scrollContainerRef, className: "overflow-y-auto focus:outline-none scrollbar-none", style: {
                                                height: '300px',
                                                scrollbarWidth: 'none',
                                                msOverflowStyle: 'none',
                                                scrollBehavior: 'auto',
                                                overscrollBehavior: 'contain',
                                                touchAction: 'pan-y'
                                            }, tabIndex: -1, onMouseMove: handleCalendarMouseMove, onMouseLeave: handleCalendarMouseLeave, 
                                            // onClick={handleCalendarClick}
                                            onWheel: function (e) {
                                                // Handle wheel events specifically for the calendar container
                                                e.stopPropagation();
                                                e.preventDefault();
                                                var container = e.currentTarget;
                                                var scrollTop = container.scrollTop, scrollHeight = container.scrollHeight, clientHeight = container.clientHeight;
                                                var isScrollingDown = e.deltaY > 0;
                                                var isScrollingUp = e.deltaY < 0;
                                                var canScrollDown = scrollTop < scrollHeight - clientHeight - 1;
                                                var canScrollUp = scrollTop > 1;
                                                if ((isScrollingDown && canScrollDown) || (isScrollingUp && canScrollUp)) {
                                                    // Use throttled scroll to prevent glitches during fast scrolling
                                                    throttledScroll(container, e.deltaY);
                                                }
                                            }, children: isCalendarReady ? (_jsx("div", { style: {
                                                    height: "".concat(virtualizer.getTotalSize(), "px"),
                                                    position: 'relative',
                                                    width: '100%',
                                                }, children: virtualizer.getVirtualItems().map(function (virtualItem) {
                                                    var _a;
                                                    var monthDate = new Date(calendarData.startDate);
                                                    monthDate.setMonth(monthDate.getMonth() + virtualItem.index);
                                                    var currentValue = getCurrentCalendarValue();
                                                    return (_jsx("div", { "data-index": virtualItem.index, "data-month-date": monthDate.toISOString(), ref: function (el) {
                                                            // Set up both virtualizer measurement and our custom ref
                                                            virtualizer.measureElement(el);
                                                            if (el) {
                                                                calendarContainerRefs.current.set(virtualItem.index, el);
                                                            }
                                                            else {
                                                                calendarContainerRefs.current.delete(virtualItem.index);
                                                            }
                                                        }, onClick: handleCalendarClick, style: {
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            transform: "translateY(".concat(virtualItem.start, "px)"),
                                                        }, children: _jsx(Calendar, { activeStartDate: monthDate, onChange: handleCalendarChange, value: currentValue, selectRange: false, returnValue: "start" // Always return single dates for our custom logic
                                                            , showDoubleView: false, minDetail: "month", maxDetail: "month", maxDate: (_a = calendarData.maxDate) !== null && _a !== void 0 ? _a : calendarData.endDate, prev2Label: null, next2Label: null, prevLabel: null, nextLabel: null, calendarType: "iso8601", navigationLabel: function (_a) {
                                                                var date = _a.date;
                                                                return date.toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'long',
                                                                });
                                                            }, className: "border-none bg-white dark:bg-background px-[12px] custom-calendar-range month-clickable ".concat(editingRange === 'comparison' && isCompareOn && (compareMode === 'custom' || compareMode === 'same')
                                                                ? 'comparison-mode'
                                                                : '', " ").concat(selectionState.isSelecting ? 'multi-month-selecting' : '', " ").concat(presetOnly ? 'pointer-events-none opacity-50' : ''), tileClassName: function (_a) {
                                                                var date = _a.date, view = _a.view, activeStartDate = _a.activeStartDate;
                                                                if (view !== 'month')
                                                                    return '';
                                                                // Add weekday class for CSS grid column placement (0=Sun, 1=Mon, ..., 6=Sat)
                                                                var weekdayClass = "weekday-".concat(date.getDay());
                                                                // Add week row class for CSS grid row placement
                                                                var weekRowClass = "weekrow-".concat(getWeekRow(date, weekStartsOn));
                                                                // Check if it's a neighboring month date first and exclude it completely
                                                                var isNeighbor = date.getMonth() !== activeStartDate.getMonth();
                                                                if (isNeighbor) {
                                                                    return "w-[36px] h-[36px] text-sm font-normal flex items-center justify-center transition-colors duration-150 rounded-full invisible pointer-events-none ".concat(weekdayClass, " ").concat(weekRowClass);
                                                                }
                                                                // Get current ranges for styling
                                                                var currentRange = getCurrentCalendarValue();
                                                                var isSelecting = selectionState.isSelecting;
                                                                var selectionStart = selectionState.startDate;
                                                                var hoverDate = selectionState.hoverDate;
                                                                // Primary range checks
                                                                var isPrimaryStart = isSameDay(date, range[0]);
                                                                var isPrimaryEnd = isSameDay(date, range[1]);
                                                                var isPrimarySelected = isPrimaryStart || isPrimaryEnd;
                                                                var isInPrimaryRange = date > range[0] && date < range[1];
                                                                // Comparison range checks
                                                                var isCompareStart = isCompareOn && compareRange && isSameDay(date, compareRange[0]);
                                                                var isCompareEnd = isCompareOn && compareRange && isSameDay(date, compareRange[1]);
                                                                var isCompareSelected = isCompareStart || isCompareEnd;
                                                                var isInCompareRange = isCompareOn && compareRange && date > compareRange[0] && date < compareRange[1];
                                                                // Selection in progress checks
                                                                var isSelectionStart = isSelecting && selectionStart && isSameDay(date, selectionStart);
                                                                // Hover range calculation during selection
                                                                var isInHoverRange = false;
                                                                if (isSelecting && selectionStart && hoverDate) {
                                                                    var isStartDate = isSameDay(date, selectionStart);
                                                                    var isHoverDate = isSameDay(date, hoverDate);
                                                                    if (!isStartDate && !isHoverDate) {
                                                                        // Check if date is between start and hover
                                                                        var startTime = selectionStart.getTime();
                                                                        var hoverTime = hoverDate.getTime();
                                                                        var dateTime = date.getTime();
                                                                        isInHoverRange = (startTime < hoverTime && dateTime > startTime && dateTime < hoverTime) ||
                                                                            (startTime > hoverTime && dateTime < startTime && dateTime > hoverTime);
                                                                    }
                                                                }
                                                                var isHoverEnd = isSelecting && hoverDate && isSameDay(date, hoverDate);
                                                                // Overlapping detection
                                                                var isInBothRanges = isInPrimaryRange && isInCompareRange;
                                                                var isBothSelected = isPrimarySelected && isCompareSelected;
                                                                var isOverlapping = isInBothRanges || (isCompareSelected && isInPrimaryRange) || (isPrimarySelected && isInCompareRange) || isBothSelected;
                                                                // weekdayClass and weekRowClass are already defined above for CSS grid placement
                                                                var baseClasses = "w-[36px] h-[36px] text-sm font-normal flex items-center justify-center transition-colors duration-150 rounded-full ".concat(weekdayClass, " ").concat(weekRowClass);
                                                                // Selection in progress styling (highest priority for UX feedback)
                                                                if (isSelecting) {
                                                                    if (isSelectionStart) {
                                                                        return baseClasses + ' bg-[hsl(var(--primary-pressed-dark))] dark:bg-primary text-white dark:text-primary-foreground font-medium';
                                                                    }
                                                                    // Visual feedback for hover end date during selection
                                                                    if (isHoverEnd) {
                                                                        return baseClasses + ' bg-[hsl(var(--primary-pressed-dark))] dark:bg-primary text-white dark:text-primary-foreground font-medium';
                                                                    }
                                                                    // Visual feedback for potential range during selection (grey background)
                                                                    if (isInHoverRange) {
                                                                        return baseClasses + ' !bg-gray-300 dark:!bg-muted !text-gray-800 dark:!text-foreground hover:!bg-gray-400 dark:hover:!bg-accent';
                                                                    }
                                                                }
                                                                // Overlapping always wins (except during selection)
                                                                if (isOverlapping) {
                                                                    if (isCompareStart || isCompareEnd) {
                                                                        return baseClasses + ' overlapping-intermediate';
                                                                    }
                                                                    if (isPrimaryStart) {
                                                                        return baseClasses + ' overlapping-primary-start';
                                                                    }
                                                                    if (isPrimaryEnd) {
                                                                        return baseClasses + ' overlapping-primary-end';
                                                                    }
                                                                    return baseClasses + ' overlapping-intermediate';
                                                                }
                                                                // Compare mode: light blue for range
                                                                if (isCompareOn && compareRange) {
                                                                    if (isCompareStart)
                                                                        return baseClasses + ' compare-start';
                                                                    if (isCompareEnd)
                                                                        return baseClasses + ' compare-end';
                                                                    if (isInCompareRange)
                                                                        return baseClasses + ' compare-middle';
                                                                }
                                                                // Primary mode: light green for range
                                                                if (isPrimaryStart)
                                                                    return baseClasses + ' primary-start';
                                                                if (isPrimaryEnd)
                                                                    return baseClasses + ' primary-end';
                                                                if (isInPrimaryRange)
                                                                    return baseClasses + ' primary-middle';
                                                                // Default styling
                                                                var isToday = isSameDay(date, new Date());
                                                                if (isToday) {
                                                                    return baseClasses + ' bg-primary-light text-[#1A1A1A] dark:text-foreground';
                                                                }
                                                                else {
                                                                    // Add a semantic class for hover, handled in CSS
                                                                    return baseClasses + ' calendar-tile-default';
                                                                }
                                                            } }, "".concat(monthDate.toISOString(), "-").concat(editingRange, "-").concat(compareMode, "-").concat(selectionState.isSelecting)) }, virtualItem.index));
                                                }) })) : (_jsx("div", { className: "flex items-center justify-center h-full", children: _jsx("div", { className: "text-gray-500 dark:text-muted-foreground", children: "Loading calendar..." }) })) }), _jsxs("div", { className: "flex justify-end mt-3 space-x-3", children: [_jsx(Button, { variant: "ghost", onClick: handleCancel, children: "Cancel" }), _jsx(Button, { onClick: handleApply, children: "Apply" })] })] })] }) })] }))] }));
};
export default DateRangePicker;
